(function (global) {
  'use strict';

  if (!global || !global.document) return;

  const DOCUMENT = global.document;
  const CACHE_LIMIT = 12;
  const SECTION_ID = 'theme-quick-view';
  const TRIGGER_SELECTOR = '[data-theme-quick-view-trigger]';
  const ROOT_SELECTOR = '[data-theme-quick-view-fragment]';
  const GALLERY_IMAGE_SIZES = '(max-width: 749px) calc(100vw - 24px), 52vw';
  const SHEET_DRAG_DISTANCE_RATIO = 0.28;
  const SHEET_FLICK_MIN_DISTANCE = 48;
  const SHEET_FLICK_MIN_VELOCITY = 0.55;
  const SHEET_DRAG_DIRECTION_LOCK = 10;
  const SHEET_MOTION_DURATION = 180;
  const SHEET_DRAG_EXCLUSION_SELECTOR = 'button,a,input,select,textarea,[contenteditable="true"],[data-theme-quick-view-option]';
  const GALLERY_DRAG_EXCLUSION_SELECTOR = 'button,a,input,select,textarea,[contenteditable="true"],[data-theme-quick-view-option]';
  const GALLERY_SWIPE_DIRECTION_LOCK = SHEET_DRAG_DIRECTION_LOCK;
  const GALLERY_DRAG_DISTANCE_RATIO = 0.22;
  const GALLERY_DRAG_MIN_DISTANCE = 48;
  const GALLERY_FLICK_MIN_DISTANCE = 36;
  const GALLERY_FLICK_MIN_VELOCITY = 0.45;
  const GALLERY_TRANSITION_TIMEOUT = 300;
  const GALLERY_RESOURCE_LIMIT = 6;
  const QUICK_VIEW_MEDIA_MINIMUMS = {
    portrait: { regular: 168, compact: 120, critical: 96 },
    landscape: { regular: 176, compact: 120, critical: 96 }
  };

  // This is deliberately independent of product content. The sheet gives media
  // the remainder after measuring the real title, controls, and actions.
  function quickViewLayoutFit({ sheetHeight = 0, mediaChrome = 0, detailsHeight = 0, mediaMode = 'portrait' } = {}) {
    const mode = mediaMode === 'landscape' ? 'landscape' : 'portrait';
    const available = Math.max(0, Math.floor(Number(sheetHeight) - Number(mediaChrome) - Number(detailsHeight)));
    const minimums = QUICK_VIEW_MEDIA_MINIMUMS[mode];
    const density = available >= minimums.regular ? 'regular' :
      (available >= minimums.compact ? 'compact' : (available >= minimums.critical ? 'critical' : 'overflow'));
    // Overflow retains the critical media minimum and every purchase control.
    // It is only selected after the actual compacted details cannot coexist
    // with that protected minimum inside the capped sheet.
    const minimum = density === 'overflow' ? minimums.critical : minimums[density];
    return { density, available, mediaSize: Math.max(minimum, available), minimum };
  }

  class ThemeQuickViewController {
    constructor(documentRef = DOCUMENT, windowRef = global) {
      this.document = documentRef;
      this.window = windowRef;
      this.dialog = null;
      this.activeTrigger = null;
      this.activeFallbackLink = null;
      this.activeKey = null;
      this.activeRequest = null;
      this.requestSerial = 0;
      this.cache = new Map();
      this.inflight = new Map();
      this.productState = null;
      this.linkedColorFamilyUnion = null;
      this.galleryWarmupKeys = new Set();
      this.galleryWarmupImages = new Map();
      this.galleryWarmupSequence = 0;
      this.galleryGesture = null;
      this.galleryInteraction = null;
      this.galleryInteractionSerial = 0;
      this.galleryEventPhases = new WeakMap();
      this.galleryAnimationFrame = null;
      this.galleryPendingOffset = 0;
      this.galleryClickSuppression = false;
      this.galleryClickTimer = null;
      this.galleryPresentationQuery = null;
      this.galleryReducedMotionQuery = null;
      this.backdropPointerDown = false;
      this.initialized = false;
      this.dialogListenersBound = false;
      this.fallbackFocusHandler = null;
      this.readinessObserver = null;
      this.readinessObserverTarget = null;
      this.pendingSwapFocus = null;
      this.fallbackLinkLabel = '';
      this.sheetDrag = null;
      this.sheetMotionTimer = null;
      this.sheetDragListenersBound = false;
      this.sheetResizeListenerBound = false;
      this.layoutFitObserver = null;
      this.layoutFitFrame = null;
      this.layoutFitFrameCancel = null;
      this.layoutFitRoot = null;
      this.dialog?.style?.removeProperty?.('block-size');
      this.dialog?.removeAttribute?.('data-theme-quick-view-note-envelope');
      this.layoutFitMediaSettle = null;
      this.lightbox = null;
      this.lightboxIndex = 0;
      this.lightboxZoom = 1;
      this.lightboxViewer = null;
      this.lowStockStale = false;
    }

    init() {
      if (this.initialized) {
        this.refreshDialog();
        this.markReady();
        this.bindReadinessObserver();
        return this;
      }

      this.initialized = true;
      this.document.addEventListener('click', (event) => this.handleDocumentClick(event));
      this.document.addEventListener('keydown', (event) => this.handleDocumentKeydown(event));
      this.document.addEventListener('theme:low-stock:invalidate', () => {
        this.lowStockStale = true;
        this.cache.clear();
        const cue = this.productState?.root?.querySelector('[data-theme-low-stock]');
        if (cue) {
          cue.textContent = '';
          cue.hidden = true;
        }
      });
      this.refreshDialog();
      this.markReady();
      this.bindReadinessObserver();
      return this;
    }

    refreshDialog() {
      const nextDialog = this.document.querySelector('[data-theme-quick-view-dialog]');
      if (!nextDialog) return null;

      this.dialog = nextDialog;
      if (this.dialogListenersBound) return this.dialog;
      this.dialogListenersBound = true;

      this.dialog.addEventListener('click', (event) => this.handleDialogClick(event));
      this.dialog.addEventListener('change', (event) => this.handleDialogChange(event));
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropPointerDown = event.target === this.dialog;
      });
      this.dialog.addEventListener('pointerup', (event) => {
        const safeBackdropClick = this.backdropPointerDown && event.target === this.dialog;
        this.backdropPointerDown = false;
        if (safeBackdropClick) this.close();
      });
      this.dialog.addEventListener('pointercancel', () => {
        this.backdropPointerDown = false;
      });
      this.dialog.addEventListener('cancel', (event) => {
        if (event.target !== this.dialog) return;
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('submit', (event) => this.handleDialogSubmit(event));
      this.dialog.addEventListener('cancel', (event) => {
        if (event.target !== this.lightbox) return;
        event.preventDefault();
        this.closeLightbox();
      }, true);
      this.bindSheetDrag();
      return this.dialog;
    }

    bindSheetDrag() {
      const panel = this.dialog?.querySelector('[data-theme-quick-view-panel]');
      if (!panel || this.sheetDragListenersBound) return;
      this.sheetDragListenersBound = true;
      panel.addEventListener('pointerdown', (event) => this.handleSheetPointerDown(event));
      panel.addEventListener('pointermove', (event) => this.handleSheetPointerMove(event));
      panel.addEventListener('pointerup', (event) => this.handleSheetPointerUp(event));
      panel.addEventListener('pointercancel', (event) => {
        if (event.pointerType === 'pen') this.reduceGalleryPenEvent(event, 'panel', 'cancel');
        else this.cancelSheetDrag();
      });
      panel.addEventListener('lostpointercapture', (event) => this.handleSheetLostPointerCapture(event));
      panel.addEventListener('touchstart', (event) => this.handleSheetTouchStart(event), { passive: true });
      panel.addEventListener('touchmove', (event) => this.handleSheetTouchMove(event), { passive: false });
      panel.addEventListener('touchend', (event) => this.handleSheetTouchEnd(event));
      panel.addEventListener('touchcancel', (event) => this.reduceGalleryTouchEvent(event, 'panel', 'cancel'));
      if (!this.sheetResizeListenerBound && typeof this.window.addEventListener === 'function') {
        this.sheetResizeListenerBound = true;
        this.window.addEventListener('resize', () => {
          this.resetQuickViewGestures();
          this.scheduleLayoutFit();
          if (this.lightbox?.open) this.applyLightboxTransform();
        });
        this.window.addEventListener('orientationchange', () => this.scheduleLayoutFit());
        if (this.window.visualViewport && typeof this.window.visualViewport.addEventListener === 'function') {
          this.window.visualViewport.addEventListener('resize', () => {
            this.resetQuickViewGestures();
            this.scheduleLayoutFit();
          });
        }
        if (typeof this.window.matchMedia === 'function') {
          this.galleryPresentationQuery = this.window.matchMedia('(max-width: 749px)');
          const onPresentationChange = () => this.resetQuickViewGestures();
          if (typeof this.galleryPresentationQuery.addEventListener === 'function') this.galleryPresentationQuery.addEventListener('change', onPresentationChange);
          else if (typeof this.galleryPresentationQuery.addListener === 'function') this.galleryPresentationQuery.addListener(onPresentationChange);
          this.galleryReducedMotionQuery = this.window.matchMedia('(prefers-reduced-motion: reduce)');
          if (typeof this.galleryReducedMotionQuery.addEventListener === 'function') this.galleryReducedMotionQuery.addEventListener('change', onPresentationChange);
          else if (typeof this.galleryReducedMotionQuery.addListener === 'function') this.galleryReducedMotionQuery.addListener(onPresentationChange);
        }
      }
    }

    isBottomSheetPresentation() {
      if (!this.dialog || typeof this.window.getComputedStyle !== 'function') return false;
      return this.window.getComputedStyle(this.dialog)
        .getPropertyValue('--theme-quick-view-presentation')
        .trim() === 'sheet';
    }

    clearLayoutFit() {
      if (this.layoutFitFrame != null) this.layoutFitFrameCancel?.(this.layoutFitFrame);
      this.layoutFitFrame = null;
      this.layoutFitFrameCancel = null;
      this.layoutFitObserver?.disconnect?.();
      this.layoutFitObserver = null;
      if (this.layoutFitRoot && this.layoutFitMediaSettle) {
        this.layoutFitRoot.removeEventListener?.('load', this.layoutFitMediaSettle, true);
        this.layoutFitRoot.removeEventListener?.('error', this.layoutFitMediaSettle, true);
      }
      this.layoutFitMediaSettle = null;
      this.layoutFitRoot = null;
      this.dialog?.style?.removeProperty?.('block-size');
      this.dialog?.removeAttribute?.('data-theme-quick-view-note-envelope');
      this.dialog?.querySelector?.('[data-theme-quick-view-panel]')?.setAttribute?.('data-theme-quick-view-overflow', 'false');
    }

    bindLayoutFit(root) {
      this.clearLayoutFit();
      this.preserveAvailabilityNoteEnvelope(root);
      if (!root?.isConnected) return;
      this.layoutFitRoot = root;
      const schedule = () => this.scheduleLayoutFit();
      this.layoutFitMediaSettle = (event) => {
        if (event.target?.matches?.('img,video')) schedule();
      };
      root.addEventListener?.('load', this.layoutFitMediaSettle, true);
      root.addEventListener?.('error', this.layoutFitMediaSettle, true);
      if (typeof this.window.ResizeObserver === 'function') {
        this.layoutFitObserver = new this.window.ResizeObserver(schedule);
        const panel = this.dialog?.querySelector?.('[data-theme-quick-view-panel]');
        [panel, root, root.querySelector?.('[data-theme-quick-view-details]'), root.querySelector?.('[data-theme-quick-view-media-column]')]
          .filter((node) => node?.isConnected)
          .forEach((node) => this.layoutFitObserver.observe(node));
      }
      const fontsReady = this.document.fonts?.ready;
      if (fontsReady?.then) fontsReady.then(schedule).catch?.(() => {});
      schedule();
    }

    preserveAvailabilityNoteEnvelope(root = this.productState?.root) {
      const note = root?.querySelector?.('[data-theme-availability-note]');
      if (!note || !this.dialog?.open) return;
      this.dialog.style?.removeProperty?.('block-size');
      this.dialog.removeAttribute?.('data-theme-quick-view-note-envelope');
      if (this.isBottomSheetPresentation()) return;
      const hidden = note.hidden;
      note.hidden = true;
      const height = this.dialog.getBoundingClientRect?.().height || 0;
      if (height > 0) {
        this.dialog.style.setProperty('block-size', `${height}px`);
        this.dialog.setAttribute('data-theme-quick-view-note-envelope', 'true');
      }
      note.hidden = hidden;
    }

    scheduleLayoutFit() {
      this.preserveAvailabilityNoteEnvelope();
      const root = this.layoutFitRoot;
      if (!root?.isConnected) return;
      if (this.layoutFitFrame != null) return;
      const run = () => {
        this.layoutFitFrame = null;
        this.layoutFitFrameCancel = null;
        this.fitQuickViewLayout();
        this.fitQuickViewEnlarge();
      };
      const useAnimationFrame = typeof this.window.requestAnimationFrame === 'function';
      const schedule = useAnimationFrame ? this.window.requestAnimationFrame : this.window.setTimeout;
      const cancel = useAnimationFrame ? this.window.cancelAnimationFrame : this.window.clearTimeout;
      this.layoutFitFrameCancel = typeof cancel === 'function' ? cancel.bind(this.window) : null;
      this.layoutFitFrame = schedule.call(this.window, run, 0);
    }

    fitQuickViewLayout() {
      const root = this.layoutFitRoot;
      const panel = this.dialog?.querySelector?.('[data-theme-quick-view-panel]');
      const mediaColumn = root?.querySelector?.('[data-theme-quick-view-media-column]');
      const media = root?.querySelector?.('[data-theme-quick-view-media]');
      const details = root?.querySelector?.('[data-theme-quick-view-details]');
      if (!root?.isConnected || !panel || !mediaColumn || !media || !details || !this.isBottomSheetPresentation()) return;
      const sheetHeight = panel.clientHeight || panel.getBoundingClientRect?.().height || 0;
      const mode = mediaColumn.classList?.contains?.('theme-quick-view__media-column--landscape') ? 'landscape' : 'portrait';
      const currentDensity = root.dataset.themeQuickViewDensity || 'regular';
      let nextDensity = 'overflow';

      // Measure each candidate with its own spacing applied. Inferring regular
      // density from compact measurements can oscillate and falsely enable
      // scrolling even though the compact layout fits.
      for (const density of ['regular', 'compact', 'critical', 'overflow']) {
        root.dataset.themeQuickViewDensity = density;
        const mediaHeight = media.getBoundingClientRect?.().height || 0;
        const mediaChrome = Math.max(0, (mediaColumn.getBoundingClientRect?.().height || 0) - mediaHeight);
        const detailsHeight = details.getBoundingClientRect?.().height || 0;
        const fit = quickViewLayoutFit({ sheetHeight, mediaChrome, detailsHeight, mediaMode: mode });
        const minimum = QUICK_VIEW_MEDIA_MINIMUMS[mode][density === 'overflow' ? 'critical' : density];
        if (density !== 'overflow' && fit.available < minimum) continue;

        root.style.setProperty('--theme-quick-view-media-block-size', `${Math.max(minimum, fit.available)}px`);
        root.style.setProperty('--theme-quick-view-layout-available', `${fit.available}px`);
        const panelOverflows = Number(panel.scrollHeight) > Number(panel.clientHeight) + 1;
        if (density !== 'overflow' && panelOverflows) continue;
        nextDensity = density;
        break;
      }
      panel.setAttribute?.('data-theme-quick-view-overflow', nextDensity === 'overflow' ? 'true' : 'false');
      if (nextDensity !== currentDensity) this.scheduleLayoutFit();
    }

    fitQuickViewEnlarge(root = this.layoutFitRoot) {
      const frame = root?.querySelector?.('[data-theme-quick-view-gallery-frame]');
      const enlarge = root?.querySelector?.('[data-theme-quick-view-enlarge]');
      const image = this.productState?.gallery?.currentImage || root?.querySelector?.('[data-gallery-slide-role="current"] img');
      if (!frame || !enlarge) return;
      enlarge.hidden = true;
      if (!image || image.hidden || image.__themeQuickViewBroken || !image.complete || !image.naturalWidth || !image.naturalHeight) return;
      const style = this.window.getComputedStyle(image);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      const paddingTop = parseFloat(style.paddingTop) || 0;
      const boxWidth = image.clientWidth - paddingLeft - (parseFloat(style.paddingRight) || 0);
      const boxHeight = image.clientHeight - paddingTop - (parseFloat(style.paddingBottom) || 0);
      if (boxWidth <= 0 || boxHeight <= 0 || style.objectFit !== 'contain') return;
      const scale = Math.min(boxWidth / image.naturalWidth, boxHeight / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      const position = style.objectPosition.split(/\s+/);
      const offset = (value, space) => {
        if (value === 'center') return space / 2;
        if (value === 'right' || value === 'bottom') return space;
        if (value === 'left' || value === 'top') return 0;
        return value.endsWith('%') ? space * parseFloat(value) / 100 : parseFloat(value) || 0;
      };
      // Layout offsets exclude the sliding transform; navigation keeps its fixed holder.
      const left = image.offsetLeft + image.clientLeft + paddingLeft + offset(position[0], boxWidth - width);
      const top = image.offsetTop + image.clientTop + paddingTop + offset(position[1] || '50%', boxHeight - height);
      enlarge.style.right = `calc(100% - ${left + width}px + 12px)`;
      enlarge.style.bottom = `calc(100% - ${top + height}px + 12px)`;
      enlarge.hidden = false;
    }

    prefersReducedMotion() {
      return typeof this.window.matchMedia === 'function' && this.window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    sheetPointFromTouchList(touches, identifier = null) {
      const candidates = Array.from(touches || []);
      const touch = identifier == null ? candidates[0] : candidates.find((candidate) => candidate.identifier === identifier);
      return touch ? { x: touch.clientX, y: touch.clientY, identifier: touch.identifier } : null;
    }

    canStartSheetDrag(target) {
      if (this.lightbox?.open || !this.isOpen() || !this.isBottomSheetPresentation() || !target?.closest) return false;
      if (target.closest(SHEET_DRAG_EXCLUSION_SELECTOR)) return false;
      if (target.closest('[data-theme-quick-view-sheet-header]')) return true;
      const panel = this.dialog.querySelector('[data-theme-quick-view-panel]');
      return Boolean(panel && panel.getAttribute?.('data-theme-quick-view-overflow') !== 'true');
    }

    startSheetDrag(target, point, timeStamp, inputType, pointerId = null) {
      if (!point || !this.canStartSheetDrag(target)) return false;
      const fromHeader = Boolean(target.closest('[data-theme-quick-view-sheet-header]'));
      this.clearSheetMotion();
      this.sheetDrag = {
        inputType,
        pointerId,
        touchIdentifier: point.identifier ?? null,
        startX: point.x,
        startY: point.y,
        lastY: point.y,
        startTime: Number(timeStamp) || 0,
        lastTime: Number(timeStamp) || 0,
        distance: 0,
        velocity: 0,
        active: false,
        fromHeader
      };
      return true;
    }

    moveSheetDrag(point, timeStamp, event) {
      const drag = this.sheetDrag;
      if (!drag || !point || !this.isBottomSheetPresentation()) {
        if (drag) this.cancelSheetDrag({ immediate: true });
        return false;
      }
      const deltaX = point.x - drag.startX;
      const deltaY = point.y - drag.startY;
      if (!drag.active) {
        const absoluteX = Math.abs(deltaX);
        const absoluteY = Math.abs(deltaY);
        if (Math.max(absoluteX, absoluteY) < SHEET_DRAG_DIRECTION_LOCK) return false;
        if (deltaY < 0 || absoluteX > absoluteY) {
          this.cancelSheetDrag({ immediate: true });
          return false;
        }
        drag.active = true;
        this.dialog.classList.add('is-sheet-dragging');
      }
      const distance = Math.max(0, deltaY);
      const elapsed = Math.max(1, (Number(timeStamp) || drag.lastTime) - drag.lastTime);
      const segmentVelocity = Math.max(0, point.y - drag.lastY) / elapsed;
      drag.velocity = drag.velocity ? (drag.velocity * 0.65) + (segmentVelocity * 0.35) : segmentVelocity;
      drag.distance = distance;
      drag.lastY = point.y;
      drag.lastTime = Number(timeStamp) || drag.lastTime;
      this.dialog.style.setProperty('--theme-quick-view-sheet-drag-offset', `${distance}px`);
      if (event?.cancelable) event.preventDefault();
      return true;
    }

    finishSheetDrag(point, timeStamp) {
      const drag = this.sheetDrag;
      if (!drag) return false;
      if (point) this.moveSheetDrag(point, timeStamp, null);
      if (!this.sheetDrag) return false;
      const panel = this.dialog?.querySelector('[data-theme-quick-view-panel]');
      const sheetHeight = panel?.getBoundingClientRect?.().height || this.dialog?.getBoundingClientRect?.().height || 0;
      const distanceClose = sheetHeight > 0 && drag.distance >= sheetHeight * SHEET_DRAG_DISTANCE_RATIO;
      const flickClose = drag.distance >= SHEET_FLICK_MIN_DISTANCE && drag.velocity >= SHEET_FLICK_MIN_VELOCITY;
      this.sheetDrag = null;
      if (drag.active && (distanceClose || flickClose)) {
        this.dismissSheetFromDrag(sheetHeight);
        return true;
      }
      this.snapSheetBack(drag.active);
      return false;
    }

    dismissSheetFromDrag(sheetHeight) {
      if (!this.dialog) return;
      this.dialog.classList.remove('is-sheet-dragging', 'is-sheet-snapping');
      if (this.prefersReducedMotion()) {
        this.close();
        return;
      }
      this.dialog.classList.add('is-sheet-dismissing');
      this.dialog.style.setProperty('--theme-quick-view-sheet-drag-offset', `${Math.max(sheetHeight, 1)}px`);
      this.sheetMotionTimer = this.window.setTimeout(() => this.close(), SHEET_MOTION_DURATION);
    }

    snapSheetBack(animate = true) {
      if (!this.dialog) return;
      this.dialog.classList.remove('is-sheet-dragging', 'is-sheet-dismissing');
      this.dialog.style.setProperty('--theme-quick-view-sheet-drag-offset', '0px');
      if (!animate || this.prefersReducedMotion()) {
        this.dialog.classList.remove('is-sheet-snapping');
        return;
      }
      this.dialog.classList.add('is-sheet-snapping');
      this.sheetMotionTimer = this.window.setTimeout(() => this.clearSheetMotion(), SHEET_MOTION_DURATION);
    }

    clearSheetMotion() {
      if (this.sheetMotionTimer != null && typeof this.window.clearTimeout === 'function') {
        this.window.clearTimeout(this.sheetMotionTimer);
      }
      this.sheetMotionTimer = null;
      if (!this.dialog) return;
      this.dialog.classList?.remove?.('is-sheet-dragging', 'is-sheet-snapping', 'is-sheet-dismissing');
      this.dialog.style?.removeProperty?.('--theme-quick-view-sheet-drag-offset');
    }

    cancelSheetDrag({ immediate = false } = {}) {
      const wasActive = Boolean(this.sheetDrag?.active);
      this.sheetDrag = null;
      if (immediate) this.clearSheetMotion();
      else this.snapSheetBack(wasActive);
    }

    handleSheetPointerDown(event) {
      if (event.pointerType === 'touch') return;
      if (event.pointerType === 'pen') {
        this.reduceGalleryPenEvent(event, 'panel', 'start');
        return;
      }
      if (event.isPrimary === false || event.button > 0) return;
      this.startSheetDrag(event.target, { x: event.clientX, y: event.clientY }, event.timeStamp, 'pointer', event.pointerId);
    }

    handleSheetPointerMove(event) {
      if (event.pointerType === 'pen') {
        this.reduceGalleryPenEvent(event, 'panel', 'move');
        return;
      }
      if (this.sheetDrag?.inputType !== 'pointer' || this.sheetDrag.pointerId !== event.pointerId) return;
      if (this.moveSheetDrag({ x: event.clientX, y: event.clientY }, event.timeStamp, event) && typeof event.currentTarget?.setPointerCapture === 'function') {
        try { event.currentTarget.setPointerCapture(event.pointerId); } catch (error) { /* Pointer capture is best effort. */ }
      }
    }

    handleSheetPointerUp(event) {
      if (event.pointerType === 'pen') {
        this.reduceGalleryPenEvent(event, 'panel', 'end');
        return;
      }
      if (this.sheetDrag?.inputType !== 'pointer' || this.sheetDrag.pointerId !== event.pointerId) return;
      this.finishSheetDrag({ x: event.clientX, y: event.clientY }, event.timeStamp);
    }

    handleSheetLostPointerCapture(event) {
      if (event.pointerType === 'pen') {
        this.reduceGalleryPenEvent(event, 'panel', 'cancel');
        return;
      }
      if (this.sheetDrag?.inputType === 'pointer' && this.sheetDrag.pointerId === event.pointerId) this.cancelSheetDrag();
    }

    handleSheetTouchStart(event) {
      this.reduceGalleryTouchEvent(event, 'panel', 'start');
    }

    handleSheetTouchMove(event) {
      this.reduceGalleryTouchEvent(event, 'panel', 'move');
    }

    handleSheetTouchEnd(event) {
      this.reduceGalleryTouchEvent(event, 'panel', 'end');
    }

    markReady() {
      const root = this.document.documentElement;
      if (root && root.classList) root.classList.add('theme-quick-view-ready');
      this.document.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
        this.markTriggerReady(trigger);
      });
    }

    markTriggerReady(trigger) {
      if (!trigger || typeof trigger.setAttribute !== 'function') return;
      trigger.setAttribute('data-theme-quick-view-ready', 'true');
      if (typeof trigger.removeAttribute === 'function') trigger.removeAttribute('hidden');
    }

    markReadyNodes(records) {
      for (const record of records || []) {
        for (const node of record.addedNodes || []) {
          if (!node || (node.nodeType !== 1 && node.nodeType !== 11)) continue;
          if (typeof node.matches === 'function' && node.matches(TRIGGER_SELECTOR)) this.markTriggerReady(node);
          if (typeof node.querySelectorAll === 'function') {
            node.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => this.markTriggerReady(trigger));
          }
        }
      }
    }

    bindReadinessObserver() {
      if (this.readinessObserver || typeof this.window.MutationObserver !== 'function') return;
      const target = this.document.body || this.document.documentElement;
      if (!target) return;

      this.readinessObserverTarget = target;
      this.readinessObserver = new this.window.MutationObserver((records) => this.markReadyNodes(records));
      this.readinessObserver.observe(target, { childList: true, subtree: true });
    }

    handleDocumentClick(event) {
      const trigger = event.target && event.target.closest ? event.target.closest(TRIGGER_SELECTOR) : null;
      if (!trigger) return;
      if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;

      event.preventDefault();
      this.dispatchQuickViewOpen(trigger);
      this.open(trigger);
    }

    dispatchQuickViewOpen(trigger) {
      // Packet A normally supplies the complete delegated-event payload on
      // the trigger. Keep a narrow runtime bridge for already-rendered cards
      // from an older fragment: it runs after the click has propagated, so a
      // finalized static payload can never be published twice.
      if (trigger.hasAttribute('data-theme-event') || typeof this.window.__themeDispatchFromElement !== 'function') return;

      const link = trigger.closest('[data-theme-product-card]')?.querySelector('[data-theme-product-link]');
      const get = (attribute, eventAttribute, fallback = '') => trigger.getAttribute(attribute) || link?.getAttribute(eventAttribute || attribute) || fallback;
      const shop = this.window.Shopify && this.window.Shopify.shop;
      const store = get('data-theme-quick-view-store', 'data-theme-event-store', shop || this.document.body?.getAttribute('data-shop-permanent-domain') || this.window.location.host);
      const fields = {
        'data-theme-event': 'quick_view_open',
        'data-theme-event-schema-version': 'bweb014_v1',
        'data-theme-event-store': store,
        'data-theme-event-surface': get('data-theme-quick-view-surface', 'data-theme-event-surface'),
        'data-theme-event-item-id': get('data-theme-quick-view-product-id', 'data-theme-event-item-id'),
        'data-theme-event-item-name': get('data-theme-quick-view-item-name', 'data-theme-event-item-name', link?.querySelector('.theme-product-card__title')?.textContent || ''),
        'data-theme-event-item-category': get('data-theme-quick-view-item-category', 'data-theme-event-item-category'),
        'data-theme-event-list-id': get('data-theme-quick-view-list-id', 'data-theme-event-list-id'),
        'data-theme-event-list-name': get('data-theme-quick-view-list-name', 'data-theme-event-list-name'),
        'data-theme-event-position-index': get('data-theme-quick-view-position-index', 'data-theme-event-position-index'),
        'data-theme-event-trigger-context': get('data-theme-quick-view-trigger-context', 'data-theme-event-trigger-context', 'card')
      };

      const dispatch = () => {
        const previous = new Map();
        Object.entries(fields).forEach(([attribute, value]) => {
          previous.set(attribute, trigger.getAttribute(attribute));
          if (value) trigger.setAttribute(attribute, value);
        });
        this.window.__themeDispatchFromElement(trigger);
        previous.forEach((value, attribute) => {
          if (value == null) trigger.removeAttribute(attribute);
          else trigger.setAttribute(attribute, value);
        });
      };

      if (typeof this.window.queueMicrotask === 'function') this.window.queueMicrotask(dispatch);
      else Promise.resolve().then(dispatch);
    }

    handleDocumentKeydown(event) {
      if (event.defaultPrevented) return;
      if (this.lightbox?.open) {
        if (event.key === 'Escape' || event.code === 'Escape') {
          event.preventDefault();
          this.closeLightbox();
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigateLightbox(event.key === 'ArrowLeft' ? -1 : 1);
        } else if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          this.setLightboxZoom(this.lightboxZoom + 0.25);
        } else if (event.key === '-') {
          event.preventDefault();
          this.setLightboxZoom(this.lightboxZoom - 0.25);
        } else if (event.key === '0') {
          event.preventDefault();
          this.setLightboxZoom(1);
        }
        return;
      }
      if (!this.isOpen()) return;
      if (event.key === 'Escape' || event.code === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const gallery = this.dialog?.querySelector('[data-theme-quick-view-gallery]');
      if (!gallery || !gallery.contains(event.target)) return;
      if (event.target?.closest?.('input,textarea,select,[contenteditable="true"],[data-theme-quick-view-option]')) return;

      event.preventDefault();
      this.requestNavigation(event.key === 'ArrowLeft' ? -1 : 1, 'keyboard');
    }

    handleGalleryPointerDown(event) {
      if (event.pointerType !== 'pen') return;
      this.reduceGalleryPenEvent(event, 'gallery', 'start');
    }

    handleGalleryPointerMove(event) {
      if (event.pointerType !== 'pen') return;
      this.reduceGalleryPenEvent(event, 'gallery', 'move');
    }

    handleGalleryPointerUp(event) {
      if (event.pointerType !== 'pen') return;
      this.reduceGalleryPenEvent(event, 'gallery', 'end');
    }

    handleGalleryPointerCancel(event) {
      if (event.pointerType && event.pointerType !== 'pen') return;
      this.reduceGalleryPenEvent(event, 'gallery', 'cancel');
    }

    handleGalleryTouchStart(event) {
      this.reduceGalleryTouchEvent(event, 'gallery', 'start');
    }

    handleGalleryTouchMove(event) {
      this.reduceGalleryTouchEvent(event, 'gallery', 'move');
    }

    handleGalleryTouchEnd(event) {
      this.reduceGalleryTouchEvent(event, 'gallery', 'end');
    }

    handleGalleryTouchCancel(event) {
      this.reduceGalleryTouchEvent(event, 'gallery', 'cancel');
    }

    galleryEventPhase(event, surface) {
      let phase = this.galleryEventPhases.get(event);
      if (!phase) {
        phase = {
          seenSurfaces: new Set(),
          candidateMetadata: new Set(),
          candidateSnapshotComplete: false,
          actionsApplied: false,
          terminalCleanupApplied: false
        };
        this.galleryEventPhases.set(event, phase);
      }
      phase.seenSurfaces.add(surface);
      return phase;
    }

    galleryCandidatesForEvent(event) {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const target = event.target || path[0] || null;
      const closest = (selector) => {
        const match = target?.closest?.(selector);
        if (match) return match;
        return path.find((node) => node?.matches?.(selector)) || null;
      };
      const frame = closest('[data-theme-quick-view-gallery-frame]');
      const interactive = closest(GALLERY_DRAG_EXCLUSION_SELECTOR);
      return {
        target,
        frame,
        gallery: Boolean(frame && !interactive),
        sheet: Boolean(!interactive && this.canStartSheetDrag(target)),
        scroll: true
      };
    }

    reduceGalleryTouchEvent(event, surface, stage) {
      const phase = this.galleryEventPhase(event, surface);
      const candidates = this.galleryCandidatesForEvent(event);
      if (candidates.gallery) phase.candidateMetadata.add('gallery');
      if (candidates.sheet) phase.candidateMetadata.add('sheet');
      phase.candidateMetadata.add('scroll');
      phase.candidateSnapshotComplete = true;

      if (stage === 'cancel' || (event.touches && event.touches.length > 1)) {
        if (!phase.terminalCleanupApplied) {
          phase.terminalCleanupApplied = true;
          this.cancelGalleryGesture({ immediate: true });
          this.cancelSheetDrag({ immediate: true });
          this.galleryGesture = null;
        }
        return false;
      }
      if (stage === 'end') {
        if (phase.terminalCleanupApplied) return false;
        phase.terminalCleanupApplied = true;
        const gesture = this.galleryGesture;
        const point = gesture ? this.sheetPointFromTouchList(event.changedTouches, gesture.touchIdentifier) : null;
        return this.finishSharedGalleryGesture(point, event.timeStamp);
      }
      if (phase.actionsApplied) return false;
      phase.actionsApplied = true;

      if (stage === 'start') {
        if (event.touches?.length !== 1 || (!candidates.gallery && !candidates.sheet)) return false;
        const point = this.sheetPointFromTouchList(event.touches);
        if (!point) return false;
        this.galleryGesture = {
          inputType: 'touch',
          touchIdentifier: point.identifier,
          source: candidates.target,
          frame: candidates.frame,
          candidates,
          lockedOwner: null,
          startX: point.x,
          startY: point.y,
          lastX: point.x,
          lastY: point.y,
          startTime: Number(event.timeStamp) || 0,
          lastTime: Number(event.timeStamp) || 0,
          velocityX: 0
        };
        if (candidates.sheet) this.startSheetDrag(candidates.target, point, event.timeStamp, 'touch');
        return true;
      }

      const gesture = this.galleryGesture;
      if (!gesture || gesture.inputType !== 'touch') return false;
      const point = this.sheetPointFromTouchList(event.touches, gesture.touchIdentifier);
      return this.moveSharedGalleryGesture(point, event.timeStamp, event);
    }

    reduceGalleryPenEvent(event, surface, stage) {
      const phase = this.galleryEventPhase(event, surface);
      const candidates = this.galleryCandidatesForEvent(event);
      if (candidates.gallery) phase.candidateMetadata.add('gallery');
      if (candidates.sheet) phase.candidateMetadata.add('sheet');
      phase.candidateSnapshotComplete = true;
      if (stage === 'cancel') {
        if (!phase.terminalCleanupApplied) {
          phase.terminalCleanupApplied = true;
          this.cancelGalleryGesture({ immediate: true });
          this.cancelSheetDrag({ immediate: true });
          this.releaseGalleryPointerCapture();
          this.galleryGesture = null;
        }
        return false;
      }
      if (stage === 'end') {
        if (phase.terminalCleanupApplied) return false;
        phase.terminalCleanupApplied = true;
        const result = this.finishSharedGalleryGesture({ x: event.clientX, y: event.clientY }, event.timeStamp);
        this.releaseGalleryPointerCapture();
        return result;
      }
      if (phase.actionsApplied) return false;
      phase.actionsApplied = true;
      if (stage === 'start') {
        if (event.isPrimary === false || event.button > 0 || (!candidates.gallery && !candidates.sheet)) return false;
        this.galleryGesture = {
          inputType: 'pen', pointerId: event.pointerId, source: candidates.target, frame: candidates.frame, candidates,
          lockedOwner: null, captureOwner: null, startX: event.clientX, startY: event.clientY,
          lastX: event.clientX, lastY: event.clientY, startTime: Number(event.timeStamp) || 0,
          lastTime: Number(event.timeStamp) || 0, velocityX: 0
        };
        if (candidates.sheet) this.startSheetDrag(candidates.target, { x: event.clientX, y: event.clientY }, event.timeStamp, 'pointer', event.pointerId);
        return true;
      }
      const gesture = this.galleryGesture;
      if (!gesture || gesture.inputType !== 'pen' || gesture.pointerId !== event.pointerId) return false;
      return this.moveSharedGalleryGesture({ x: event.clientX, y: event.clientY }, event.timeStamp, event);
    }

    moveSharedGalleryGesture(point, timeStamp, event) {
      const gesture = this.galleryGesture;
      if (!gesture || !point) return false;
      const deltaX = point.x - gesture.startX;
      const deltaY = point.y - gesture.startY;
      if (!gesture.lockedOwner) {
        const absoluteX = Math.abs(deltaX);
        const absoluteY = Math.abs(deltaY);
        if (Math.max(absoluteX, absoluteY) < GALLERY_SWIPE_DIRECTION_LOCK) return false;
        if (absoluteY >= absoluteX) {
          if (deltaY >= 0 && gesture.candidates.sheet && this.sheetDrag) gesture.lockedOwner = 'sheet';
          else {
            gesture.lockedOwner = 'scroll';
            this.cancelSheetDrag({ immediate: true });
          }
        } else if (gesture.candidates.gallery) {
          gesture.lockedOwner = 'gallery';
          this.cancelSheetDrag({ immediate: true });
          this.beginGalleryDrag(deltaX < 0 ? 1 : -1, gesture.frame);
          if (gesture.inputType === 'pen' && gesture.frame?.setPointerCapture) {
            try {
              gesture.frame.setPointerCapture(gesture.pointerId);
              gesture.captureOwner = gesture.frame;
            } catch (error) { /* Pointer capture is best effort. */ }
          }
        } else {
          gesture.lockedOwner = 'scroll';
          this.cancelSheetDrag({ immediate: true });
        }
      }

      const elapsed = Math.max(1, (Number(timeStamp) || gesture.lastTime) - gesture.lastTime);
      const segmentVelocity = (point.x - gesture.lastX) / elapsed;
      gesture.velocityX = gesture.velocityX ? (gesture.velocityX * 0.65) + (segmentVelocity * 0.35) : segmentVelocity;
      gesture.lastX = point.x;
      gesture.lastY = point.y;
      gesture.lastTime = Number(timeStamp) || gesture.lastTime;

      if (gesture.lockedOwner === 'gallery') {
        this.updateGalleryDrag(deltaX);
        if (event?.cancelable) event.preventDefault();
        return true;
      }
      if (gesture.lockedOwner === 'sheet') return this.moveSheetDrag(point, timeStamp, event);
      return false;
    }

    finishSharedGalleryGesture(point, timeStamp) {
      const gesture = this.galleryGesture;
      if (!gesture) return false;
      if (point && gesture.lockedOwner === 'gallery') {
        gesture.lastX = point.x;
        gesture.lastY = point.y;
      }
      if (gesture.captureOwner && gesture.pointerId != null && typeof gesture.captureOwner.releasePointerCapture === 'function') {
        try { gesture.captureOwner.releasePointerCapture(gesture.pointerId); } catch (error) { /* Best effort. */ }
        gesture.captureOwner = null;
      }
      this.galleryGesture = null;
      if (gesture.lockedOwner === 'gallery') {
        const deltaX = (point?.x ?? gesture.lastX) - gesture.startX;
        const frameWidth = this.galleryInteraction?.frameWidth || gesture.frame?.getBoundingClientRect?.().width || 0;
        const distanceQualified = Math.abs(deltaX) >= Math.max(GALLERY_DRAG_MIN_DISTANCE, frameWidth * GALLERY_DRAG_DISTANCE_RATIO);
        const flickQualified = Math.abs(deltaX) >= GALLERY_FLICK_MIN_DISTANCE && Math.abs(gesture.velocityX) >= GALLERY_FLICK_MIN_VELOCITY;
        this.finishGalleryDrag(distanceQualified || flickQualified);
        this.armGalleryClickSuppression();
        return distanceQualified || flickQualified;
      }
      if (gesture.lockedOwner === 'sheet') return this.finishSheetDrag(point, timeStamp);
      this.cancelSheetDrag({ immediate: true });
      return false;
    }

    releaseGalleryPointerCapture() {
      const gesture = this.galleryGesture;
      if (!gesture?.captureOwner || gesture.pointerId == null || typeof gesture.captureOwner.releasePointerCapture !== 'function') return;
      try { gesture.captureOwner.releasePointerCapture(gesture.pointerId); } catch (error) { /* Best effort. */ }
      gesture.captureOwner = null;
    }

    armGalleryClickSuppression() {
      this.galleryClickSuppression = true;
      if (this.galleryClickTimer != null) this.window.clearTimeout?.(this.galleryClickTimer);
      this.galleryClickTimer = this.window.setTimeout?.(() => {
        this.galleryClickSuppression = false;
        this.galleryClickTimer = null;
      }, 400) ?? null;
    }

    bindGalleryFrame(root, state) {
      const frame = root?.querySelector('[data-theme-quick-view-gallery-frame]');
      if (!frame || frame.__themeQuickViewGalleryBound) return;
      frame.__themeQuickViewGalleryBound = true;
      frame.addEventListener('pointerdown', (event) => this.handleGalleryPointerDown(event));
      frame.addEventListener('pointermove', (event) => this.handleGalleryPointerMove(event));
      frame.addEventListener('pointerup', (event) => this.handleGalleryPointerUp(event));
      frame.addEventListener('pointercancel', (event) => this.handleGalleryPointerCancel(event));
      frame.addEventListener('lostpointercapture', (event) => this.handleGalleryPointerCancel(event));
      frame.addEventListener('touchstart', (event) => this.handleGalleryTouchStart(event), { passive: true });
      frame.addEventListener('touchmove', (event) => this.handleGalleryTouchMove(event), { passive: false });
      frame.addEventListener('touchend', (event) => this.handleGalleryTouchEnd(event));
      frame.addEventListener('touchcancel', (event) => this.handleGalleryTouchCancel(event));
      frame.__themeQuickViewGalleryState = state;
    }

    handleGalleryClick(target) {
      if (!target || !this.productState) return false;
      const previous = target.closest('[data-theme-quick-view-gallery-previous]');
      const next = target.closest('[data-theme-quick-view-gallery-next]');
      if (!previous && !next) return false;
      this.requestNavigation(previous ? -1 : 1, 'arrow');
      return true;
    }

    handleDialogClick(event) {
      const target = event.target && event.target.closest ? event.target : null;
      if (!target) return;

      if (this.lightbox?.open && this.lightbox.contains(target)) {
        if (this.lightboxViewer?.consumeClick()) return;
        if (target === this.lightbox) {
          this.closeLightbox();
          return;
        }
        const action = target.closest('[data-theme-quick-view-lightbox-close], [data-theme-quick-view-lightbox-previous], [data-theme-quick-view-lightbox-next], [data-theme-quick-view-zoom-in], [data-theme-quick-view-zoom-out], [data-theme-quick-view-zoom-reset]');
        if (action) {
          event.preventDefault();
          if (action.hasAttribute('data-theme-quick-view-lightbox-close')) this.closeLightbox();
          else if (action.hasAttribute('data-theme-quick-view-lightbox-previous')) this.navigateLightbox(-1);
          else if (action.hasAttribute('data-theme-quick-view-lightbox-next')) this.navigateLightbox(1);
          else this.setLightboxZoom(action.hasAttribute('data-theme-quick-view-zoom-reset') ? 1 : this.lightboxZoom + (action.hasAttribute('data-theme-quick-view-zoom-in') ? 0.5 : -0.5));
        }
        return;
      }

      if (target.closest('[data-theme-quick-view-enlarge]')) {
        event.preventDefault();
        this.openLightbox();
        return;
      }

      if (this.galleryClickSuppression && target.closest('[data-theme-quick-view-gallery-frame]') && !target.closest(GALLERY_DRAG_EXCLUSION_SELECTOR)) {
        this.galleryClickSuppression = false;
        if (this.galleryClickTimer != null) this.window.clearTimeout?.(this.galleryClickTimer);
        this.galleryClickTimer = null;
        event.preventDefault();
        return;
      }

      const closeButton = target.closest('[data-theme-quick-view-close]');
      if (closeButton) {
        event.preventDefault();
        this.close();
        return;
      }

      const retryButton = target.closest('[data-theme-quick-view-retry]');
      if (retryButton) {
        event.preventDefault();
        this.retry();
        return;
      }

      if (this.handleGalleryClick(target)) {
        event.preventDefault();
        return;
      }

      const optionButton = target.closest('[data-theme-quick-view-option]');
      if (optionButton && this.productState) {
        event.preventDefault();
        this.selectOption(optionButton);
        return;
      }

      const familySwatch = target.closest('[data-theme-quick-view-family-swatch]');
      if (familySwatch) {
        event.preventDefault();
        this.dispatchQuickViewOpen(familySwatch);
        this.swapFamilyProduct(familySwatch);
      }
    }

    handleDialogChange(event) {
      const select = event.target?.closest?.('[data-theme-quick-view-option-select]');
      if (!select || !this.productState) return;
      this.selectOptionSelect(select);
    }

    handleDialogSubmit(event) {
      if (!this.productState) return;
      const form = event.target;
      if (!form || !form.matches || !form.matches('[data-theme-quick-view-form]')) return;

      const variant = this.productState.resolvedVariant;
      if (!variant || !variant.available) {
        event.preventDefault();
        this.announce(variant ? 'Sold out' : 'Unavailable combination', variant ? 'polite' : 'assertive');
      }
    }

    isOpen() {
      return Boolean(this.dialog && (this.dialog.open || this.dialog.hasAttribute('open')));
    }

    open(trigger, options = {}) {
      this.init();
      this.refreshDialog();
      if (!this.dialog || !trigger) return false;

      let endpoint;
      try {
        endpoint = this.buildEndpoint(trigger);
      } catch (error) {
        this.showError(error && error.message ? error.message : 'Unable to load this product right now.');
        return false;
      }

      // A card trigger starts a new dialog session unless this is the same
      // opener being retried. Linked-family swatches use swapFamilyProduct so
      // their warmups remain available for A -> B -> A navigation.
      const continuingSession = this.isOpen() && trigger === this.activeTrigger;
      if (!continuingSession) this.clearGalleryWarmups();

      if (this.isOpen() && this.activeKey !== endpoint.key) {
        this.abortActiveRequest();
      } else if (options.force && this.activeRequest) {
        this.abortActiveRequest();
      }

      this.activeTrigger = trigger;
      this.activeFallbackLink = this.findFallbackLink(trigger);
      this.activeLoadTrigger = trigger;
      this.expectedProductId = trigger.getAttribute('data-theme-quick-view-product-id') || '';
      this.pendingSwapFocus = null;
      this.activeKey = endpoint.key;
      this.linkedColorFamilyUnion = this.readOriginatingCardFamily(trigger);
      this.requestSerial += 1;
      const token = this.requestSerial;
      this.clearProductState();
      this.showDialog();
      this.showLoading(trigger);

      const cached = !options.force && this.readCache(endpoint.key);
      if (cached) {
        this.renderFragment(cached, endpoint.key, token);
        return true;
      }

      this.loadFragment(endpoint, token, options.force === true);
      return true;
    }

    buildEndpoint(trigger) {
      const rawUrl = trigger.getAttribute('data-theme-quick-view-product-url') ||
        trigger.getAttribute('data-product-url') ||
        trigger.getAttribute('data-url') ||
        trigger.closest('[data-theme-product-card]')?.querySelector('[data-theme-product-link]')?.getAttribute('href');
      if (!rawUrl) throw new Error('Unable to load this product right now.');

      const source = new URL(rawUrl, this.window.location.origin);
      if (source.origin !== this.window.location.origin) {
        throw new Error('Unable to load this product right now.');
      }

      const requestUrl = new URL(source.href);
      // Shopify establishes unpublished-theme previews through a same-domain
      // session cookie. Re-sending preview entry-point parameters on an AJAX
      // section request causes a redirect before the section is rendered.
      for (const previewParameter of ['preview_theme_id', '_fd', 'pb']) {
        requestUrl.searchParams.delete(previewParameter);
      }
      requestUrl.searchParams.set('section_id', SECTION_ID);

      const cacheUrl = new URL(requestUrl.href);
      cacheUrl.searchParams.delete('section_id');
      const cacheParams = Array.from(cacheUrl.searchParams.entries())
        .filter(([name]) => name !== 'section_id')
        .sort(([leftName, leftValue], [rightName, rightValue]) =>
          leftName.localeCompare(rightName) || leftValue.localeCompare(rightValue)
      );
      const cacheSearch = new URLSearchParams(cacheParams).toString();
      const resourceKey = cacheUrl.pathname + (cacheSearch ? '?' + cacheSearch : '');
      const key = this.resolveThemeCacheScope() + ':' + resourceKey;

      return { key, url: requestUrl.href };
    }

    resolveThemeCacheScope() {
      const shopify = this.window.Shopify;
      const theme = shopify?.theme;
      const role = typeof theme?.role === 'string' ? theme.role.trim().toLowerCase() : '';
      const themeId = theme?.id == null ? '' : String(theme.id).trim();
      return `theme-${themeId || 'unknown'}-${role || 'unknown'}${shopify?.designMode === true ? '-design' : ''}`;
    }

    loadFragment(endpoint, token, force) {
      const existing = !force && this.inflight.get(endpoint.key);
      if (existing) {
        this.activeRequest = { ...existing, token };
        existing.promise.then((fragment) => {
          if (this.isCurrentRequest(endpoint.key, token)) this.renderFragment(fragment, endpoint.key, token);
        }).catch((error) => {
          if (this.isCurrentRequest(endpoint.key, token)) this.showError(this.errorMessage(error));
        });
        return existing.promise;
      }

      const controller = typeof this.window.AbortController === 'function' ? new this.window.AbortController() : null;
      const request = { key: endpoint.key, controller, token };
      const requestOptions = {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      };
      if (controller) requestOptions.signal = controller.signal;

      const promise = this.window.fetch(endpoint.url, requestOptions)
        .then((response) => {
          if (!response || !response.ok) throw new Error('Unable to load this product right now.');
          if (response.url) {
            const finalUrl = new URL(response.url, this.window.location.origin);
            if (finalUrl.origin !== this.window.location.origin) throw new Error('Unable to load this product right now.');
          }
          return response.text();
        })
        .then((html) => {
          const fragment = this.parseFragment(html, endpoint.key);
          this.writeCache(endpoint.key, fragment);
          return fragment;
        })
        .finally(() => {
          const current = this.inflight.get(endpoint.key);
          if (current && current.promise === promise) this.inflight.delete(endpoint.key);
          if (this.activeRequest && this.activeRequest.promise === promise) this.activeRequest = null;
        });

      request.promise = promise;
      this.inflight.set(endpoint.key, request);
      this.activeRequest = request;
      promise.then((fragment) => {
        if (this.isCurrentRequest(endpoint.key, token)) this.renderFragment(fragment, endpoint.key, token);
      }).catch((error) => {
        if (this.isCurrentRequest(endpoint.key, token)) this.showError(this.errorMessage(error));
      });
      return promise;
    }

    parseFragment(html, expectedKey) {
      if (typeof html !== 'string' || !html.trim()) throw new Error('Unable to load this product right now.');
      const parser = new this.window.DOMParser();
      const parsed = parser.parseFromString(html, 'text/html');
      const roots = Array.from(parsed.querySelectorAll(ROOT_SELECTOR));
      if (roots.length !== 1) throw new Error('Unable to load this product right now.');
      const root = roots[0];
      const productId = root.getAttribute('data-theme-quick-view-product-id');
      if (!productId) throw new Error('Unable to load this product right now.');
      const sectionId = root.getAttribute('data-theme-quick-view-section-id');
      if (sectionId && sectionId !== SECTION_ID) throw new Error('Unable to load this product right now.');

      const matrixNodes = Array.from(root.querySelectorAll('[data-theme-quick-view-variants]'));
      if (matrixNodes.length !== 1) throw new Error('Unable to load this product right now.');
      let matrix;
      try {
        matrix = JSON.parse(matrixNodes[0].textContent || '[]');
      } catch (error) {
        throw new Error('Unable to load this product right now.');
      }
      if (!Array.isArray(matrix) || !matrix.length || matrix.some((variant) => !variant || variant.id == null || !Array.isArray(variant.options))) {
        throw new Error('Unable to load this product right now.');
      }
      const ids = matrix.map((variant) => String(variant.id));
      if (new Set(ids).size !== ids.length) throw new Error('Unable to load this product right now.');

      const galleryNodes = Array.from(root.querySelectorAll('[data-theme-quick-view-images]'));
      if (galleryNodes.length !== 1) throw new Error('Unable to load this product right now.');
      let galleryImages;
      try {
        galleryImages = JSON.parse(galleryNodes[0].textContent || '[]');
      } catch (error) {
        throw new Error('Unable to load this product right now.');
      }
      if (!Array.isArray(galleryImages)) throw new Error('Unable to load this product right now.');

      const imageIds = new Set();
      for (const image of galleryImages) {
        if (!image || image.id == null || typeof image.src !== 'string' || !image.src.trim() || !this.isTrustedImageUrl(image.src)) {
          throw new Error('Unable to load this product right now.');
        }
        if (image.zoomSrc != null && (typeof image.zoomSrc !== 'string' || !this.isTrustedImageUrl(image.zoomSrc))) {
          throw new Error('Unable to load this product right now.');
        }
        const imageId = String(image.id);
        if (imageIds.has(imageId)) throw new Error('Unable to load this product right now.');
        imageIds.add(imageId);
        if (!String(image.alt || '').trim()) throw new Error('Unable to load this product right now.');
        const imageWidth = Number(image.width);
        const imageHeight = Number(image.height);
        if (!Number.isInteger(imageWidth) || imageWidth <= 0 || !Number.isInteger(imageHeight) || imageHeight <= 0) {
          throw new Error('Unable to load this product right now.');
        }
        if (!Array.isArray(image.srcset)) throw new Error('Unable to load this product right now.');
        const candidates = this.normalizedSrcset(image.srcset);
        if (candidates.length !== image.srcset.length || candidates.some((candidate) => !candidate)) {
          throw new Error('Unable to load this product right now.');
        }
        const candidateWidths = new Set();
        for (const candidate of candidates) {
          if (!this.isTrustedImageUrl(candidate.src) || candidate.width > imageWidth || candidateWidths.has(candidate.width)) {
            throw new Error('Unable to load this product right now.');
          }
          candidateWidths.add(candidate.width);
        }
        if (!candidateWidths.has(imageWidth)) throw new Error('Unable to load this product right now.');
      }

      for (const variant of matrix) {
        const featuredImageId = variant.featured_image_id ?? variant.media?.id;
        if (featuredImageId != null && !imageIds.has(String(featuredImageId))) {
          throw new Error('Unable to load this product right now.');
        }
        if (variant.media) {
          if (!variant.media.src || !this.isTrustedImageUrl(variant.media.src) || !String(variant.media.alt || '').trim()) {
            throw new Error('Unable to load this product right now.');
          }
          for (const dimension of ['width', 'height']) {
            if (variant.media[dimension] != null && (!Number.isFinite(Number(variant.media[dimension])) || Number(variant.media[dimension]) <= 0)) {
              throw new Error('Unable to load this product right now.');
            }
          }
        }
      }

      const expectedProductId = this.expectedProductId;
      if (expectedProductId && expectedProductId !== productId) {
        throw new Error('Unable to load this product right now.');
      }
      if (!expectedKey || !root.matches(ROOT_SELECTOR)) throw new Error('Unable to load this product right now.');
      return root.outerHTML;
    }

    normalizedSrcset(srcset) {
      const candidates = Array.isArray(srcset)
        ? srcset
        : (typeof srcset === 'string' && srcset.trim() ? srcset.split(',') : []);
      return candidates.map((candidate) => {
        if (typeof candidate === 'string') {
          const match = candidate.trim().match(/^(\S+)\s+(\d+)w$/);
          return match ? { src: match[1], width: Number(match[2]) } : null;
        }
        if (!candidate || typeof candidate !== 'object') return null;
        const src = typeof candidate.src === 'string' ? candidate.src.trim() : '';
        const width = Number(candidate.width);
        if (!src || !Number.isInteger(width) || width <= 0) return null;
        return { src, width };
      });
    }

    isTrustedImageUrl(value) {
      try {
        const parsed = new URL(String(value), this.window.location.origin);
        if (parsed.origin === this.window.location.origin) return true;
        const hostname = parsed.hostname.toLowerCase();
        return parsed.protocol === 'https:' && (
          hostname === 'shopify.com' ||
          hostname.endsWith('.shopify.com') ||
          hostname === 'myshopify.com' ||
          hostname.endsWith('.myshopify.com')
        );
      } catch (error) {
        return false;
      }
    }

    renderFragment(fragment, key, token) {
      if (!this.isCurrentRequest(key, token) || !this.dialog) return false;
      this.resetQuickViewGestures();
      let parsed;
      try {
        parsed = this.parseFragment(fragment, key);
      } catch (error) {
        this.showError(this.errorMessage(error));
        return false;
      }
      const host = this.dialog.querySelector('[data-theme-quick-view-content-host]');
      if (!host) return false;

      const template = this.document.createElement('template');
      template.innerHTML = parsed;
      const root = template.content.querySelector(ROOT_SELECTOR);
      if (!root) {
        this.showError('Unable to load this product right now.');
        return false;
      }

      const requestedProductId = this.pendingSwapFocus?.productId || this.activeLoadTrigger?.getAttribute?.('data-theme-quick-view-product-id') || this.expectedProductId || '';
      const shouldRestoreSwapFocus = this.pendingSwapFocus?.restore === true;
      this.pendingSwapFocus = null;

      this.reconcileLinkedColorFamily(root);
      this.clearLayoutFit();
      host.replaceChildren(root);
      host.setAttribute('aria-busy', 'false');
      this.setHidden('[data-theme-quick-view-loading]', true);
      this.setHidden('[data-theme-quick-view-fallback-wrap]', true);
      const title = root.querySelector('[data-theme-quick-view-title]');
      if (title && title.id) this.dialog.setAttribute('aria-labelledby', title.id);
      this.productState = this.readProductState(root, token);
      this.bindQuantityControl(root);
      this.bindGalleryFrame(root, this.productState);
      this.updateProductState();
      this.window.themePdpOptionSelect?.init(root);
      this.bindLayoutFit(root);
      if (shouldRestoreSwapFocus) {
        const matchingSwatch = Array.from(root.querySelectorAll('[data-theme-quick-view-family-swatch]'))
          .find((swatch) => swatch.getAttribute('data-theme-quick-view-product-id') === String(requestedProductId));
        const focusTarget = matchingSwatch || title || this.dialog.querySelector('[data-theme-quick-view-close]');
        if (focusTarget && focusTarget !== title && typeof focusTarget.focus === 'function') {
          this.focusWithoutScroll(focusTarget);
        } else if (title && typeof title.focus === 'function') {
          title.setAttribute('tabindex', '-1');
          this.focusWithoutScroll(title);
        } else if (focusTarget && typeof focusTarget.focus === 'function') {
          this.focusWithoutScroll(focusTarget);
        }
      }
      this.refreshFocusTrapKeepingFocus();
      this.announce('Quick view ready', 'polite');
      const galleryCount = this.productState.gallery.images.length;
      const productTitle = title?.textContent?.trim() || 'Product';
      this.announceGallery(this.productState, galleryCount ? `${productTitle}. ${galleryCount} ${galleryCount === 1 ? 'image' : 'images'}` : `${productTitle}. Product image unavailable`);
      return true;
    }

    bindQuantityControl(root) {
      const quantityControls = this.window?.ThemeQuantityControl;
      if (!quantityControls || typeof quantityControls.bind !== 'function') return null;
      const control = quantityControls.bind(root);
      control?.reset?.();
      return control;
    }

    readOriginatingCardFamily(trigger) {
      const group = trigger.closest('[data-theme-product-card]')?.querySelector('[data-theme-product-card-swatches]');
      if (!group) return null;
      const entries = [];
      const seen = new Set();
      group.querySelectorAll('[data-theme-quick-view-family-swatch]').forEach((source) => {
        const id = source.getAttribute('data-theme-quick-view-product-id');
        if (!id || seen.has(id)) return;
        seen.add(id);
        const swatch = source.cloneNode(true);
        // Card controls open a session; dialog controls swap within that session.
        swatch.removeAttribute('data-theme-quick-view-trigger');
        swatch.removeAttribute('data-theme-quick-view-ready');
        swatch.className = 'theme-quick-view__option-value theme-quick-view__option-value--swatch';
        swatch.classList.toggle('is-sold-out', source.getAttribute('data-theme-linked-color-available') !== 'true');
        swatch.setAttribute('data-theme-event-surface', 'quick_view');
        const visual = swatch.querySelector('.theme-product-card__swatch');
        if (visual) {
          visual.className = 'theme-quick-view__swatch';
          visual.style.setProperty('--theme-quick-view-swatch-color', visual.style.getPropertyValue('--theme-product-card-swatch-color'));
          visual.style.setProperty('--theme-quick-view-swatch-image', visual.style.getPropertyValue('--theme-product-card-swatch-image'));
          visual.style.removeProperty('--theme-product-card-swatch-color');
          visual.style.removeProperty('--theme-product-card-swatch-image');
        }
        entries.push({ swatch, item: this.createFamilySwatchItem(swatch) });
      });
      return entries.length ? entries : null;
    }

    reconcileLinkedColorFamily(root) {
      const group = root.querySelector('[data-theme-quick-view-linked-color-group]');
      const list = group?.querySelector('[role="list"]');
      if (!group || !list) return;

      const incoming = Array.from(list.querySelectorAll('[data-theme-quick-view-family-swatch]'));
      if (!incoming.length) return;

      const productId = root.getAttribute('data-theme-quick-view-product-id');
      const incomingEntries = incoming.map((swatch) => {
        const existingItem = swatch.parentElement && swatch.parentElement.getAttribute('role') === 'listitem'
          ? swatch.parentElement
          : null;
        const item = existingItem || this.createFamilySwatchItem(swatch);
        return { item, swatch };
      });
      const incomingById = new Map();
      const uniqueIncoming = [];
      incomingEntries.forEach((entry) => {
        const id = entry.swatch.getAttribute('data-theme-quick-view-product-id');
        if (incomingById.has(id)) return;
        incomingById.set(id, entry);
        uniqueIncoming.push(entry);
      });
      if (!this.linkedColorFamilyUnion) {
        this.linkedColorFamilyUnion = uniqueIncoming;
      } else {
        const knownIds = new Set();
        const nextUnion = [];
        this.linkedColorFamilyUnion.forEach((entry) => {
          const swatch = entry.swatch || entry;
          const item = entry.item || (swatch.parentElement && swatch.parentElement.getAttribute('role') === 'listitem'
            ? swatch.parentElement
            : this.createFamilySwatchItem(swatch));
          const id = swatch.getAttribute('data-theme-quick-view-product-id');
          if (knownIds.has(id)) return;
          knownIds.add(id);
          nextUnion.push(incomingById.get(id) || { item, swatch });
        });
        uniqueIncoming.forEach((entry) => {
          const id = entry.swatch.getAttribute('data-theme-quick-view-product-id');
          if (knownIds.has(id)) return;
          knownIds.add(id);
          nextUnion.push(entry);
        });
        this.linkedColorFamilyUnion = nextUnion;
      }

      const familyHasAvailable = this.linkedColorFamilyUnion.some(({ swatch }) => swatch.getAttribute('data-theme-linked-color-available') === 'true');
      root.setAttribute('data-theme-quick-view-family-has-available', familyHasAvailable ? 'true' : 'false');
      list.replaceChildren(...this.linkedColorFamilyUnion.map(({ item }) => item));
      this.linkedColorFamilyUnion.forEach(({ swatch }) => {
        const selected = swatch.getAttribute('data-theme-quick-view-product-id') === productId;
        swatch.classList.toggle('is-active', selected);
        swatch.setAttribute('aria-current', selected ? 'true' : 'false');
      });
    }

    createFamilySwatchItem(swatch) {
      const item = this.document.createElement('div');
      item.className = 'theme-quick-view__swatch-item';
      item.setAttribute('role', 'listitem');
      if (typeof item.append === 'function') item.append(swatch);
      else if (typeof item.appendChild === 'function') item.appendChild(swatch);
      return item;
    }

    readProductState(root, token = this.requestSerial) {
      const variantsNode = root.querySelector('[data-theme-quick-view-variants]');
      let variants = [];
      try {
        variants = JSON.parse(variantsNode?.textContent || '[]');
      } catch (error) {
        variants = [];
      }
      variants = Array.isArray(variants) ? variants.filter((variant) => variant && Array.isArray(variant.options)) : [];

      const selectedId = root.getAttribute('data-theme-quick-view-selected-variant-id');
      const selectedOptions = this.parseJsonAttribute(root, 'data-theme-quick-view-selected-options');
      const initialVariant = variants.find((variant) => String(variant.id) === String(selectedId)) ||
        variants.find((variant) => variant.available) || variants[0] || null;
      const selection = Array.isArray(selectedOptions) && selectedOptions.length ? selectedOptions.slice() :
        (initialVariant ? initialVariant.options.slice() : []);
      const images = this.readGalleryImages(root);
      const selectedImageId = root.getAttribute('data-theme-quick-view-selected-image-id') || '';
      const productFeaturedImageId = root.getAttribute('data-theme-quick-view-product-featured-image-id') || '';
      const initialImageId = selectedImageId || productFeaturedImageId;
      const initialIndex = images.findIndex((image) => image.id === String(initialImageId));

      return {
        root,
        variants,
        selection,
        resolvedVariant: null,
        productUrl: root.getAttribute('data-theme-quick-view-product-url') || '#',
        fallbackMedia: this.readFallbackMedia(root),
        gallery: {
          images,
          currentIndex: initialIndex >= 0 ? initialIndex : 0,
          productId: root.getAttribute('data-theme-quick-view-product-id') || '',
          token,
          manualSelection: false,
          renderedImageId: null,
          imageToken: 0,
          currentImage: null,
          pendingImage: null,
          brokenImageId: null
        }
      };
    }

    readGalleryImages(root) {
      const node = root?.querySelector('[data-theme-quick-view-images]');
      if (!node) return [];
      try {
        const value = JSON.parse(node.textContent || '[]');
        if (!Array.isArray(value)) return [];
        return value.map((image) => ({
          id: String(image.id),
          src: String(image.src || ''),
          zoomSrc: String(image.zoomSrc || image.src || ''),
          srcset: this.normalizedSrcset(image.srcset),
          width: image.width == null ? '' : Number(image.width),
          height: image.height == null ? '' : Number(image.height),
          alt: String(image.alt || '')
        }));
      } catch (error) {
        return [];
      }
    }

    readFallbackMedia(root) {
      const src = root.getAttribute('data-theme-quick-view-fallback-media-src');
      if (!src) return null;
      return {
        src,
        alt: root.getAttribute('data-theme-quick-view-fallback-media-alt') || '',
        width: root.getAttribute('data-theme-quick-view-fallback-media-width') || '',
        height: root.getAttribute('data-theme-quick-view-fallback-media-height') || ''
      };
    }

    parseJsonAttribute(element, name) {
      try {
        const value = JSON.parse(element.getAttribute(name) || 'null');
        return value;
      } catch (error) {
        return null;
      }
    }

    selectOption(button) {
      const position = Number(button.getAttribute('data-theme-quick-view-option-position'));
      if (!Number.isInteger(position) || position < 0 || !this.productState || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      const value = button.getAttribute('data-theme-quick-view-option-value') || '';
      this.selectOptionValue(position, value);
    }

    selectOptionSelect(select) {
      const position = Number(select.getAttribute('data-theme-quick-view-option-position'));
      if (!Number.isInteger(position) || position < 0 || !this.productState || select.disabled) return;
      const selectedOption = Array.from(select.options || []).find((option) => option.value === select.value);
      if (!selectedOption || selectedOption.disabled) return;
      this.selectOptionValue(position, select.value);
    }

    selectOptionValue(position, value) {
      if (!this.productState) return;
      const candidate = this.resolveVariantForCandidate(position, value);
      if (!candidate || candidate.available === false) return;
      this.productState.selection[position] = value;
      this.productState.userInteracted = true;
      this.updateProductState();
    }

    resolveVariant(selection) {
      if (!this.productState) return null;
      return this.productState.variants.find((variant) =>
        variant.options.length === selection.length && variant.options.every((value, index) => value === selection[index])
      ) || null;
    }

    isReachable(position, value) {
      if (!this.productState) return false;
      return this.productState.variants.some((variant) =>
        variant.options.length === this.productState.selection.length &&
        variant.options[position] === value &&
        variant.options.every((candidate, index) => index === position || candidate === this.productState.selection[index])
      );
    }

    updateProductState() {
      const state = this.productState;
      if (!state) return;
      const root = state.root;
      const previousVariant = state.resolvedVariant;
      const variant = this.resolveVariant(state.selection);
      state.resolvedVariant = variant;
      const variantChanged = Boolean(
        previousVariant &&
        variant &&
        String(previousVariant.id) !== String(variant.id)
      );

      root.querySelectorAll('[data-theme-quick-view-option]').forEach((button) => {
        const position = Number(button.getAttribute('data-theme-quick-view-option-position'));
        const value = button.getAttribute('data-theme-quick-view-option-value') || '';
        const selected = state.selection[position] === value;
        const reachable = this.isReachable(position, value);
        const candidate = this.resolveVariantForCandidate(position, value);
        const optionControl = this.window?.ThemeOptionControl;
        if (optionControl && typeof optionControl.sync === 'function') {
          optionControl.sync(button, {
            selected,
            soldOut: Boolean(reachable && candidate?.available === false),
            unavailable: !reachable
          });
        }
      });

      root.querySelectorAll('[data-theme-quick-view-option-select]').forEach((select) => {
        const position = Number(select.getAttribute('data-theme-quick-view-option-position'));
        if (!Number.isInteger(position) || position < 0) return;
        select.value = state.selection[position] || '';
        Array.from(select.options || []).forEach((option) => {
          const value = option.value;
          const reachable = this.isReachable(position, value);
          const candidate = this.resolveVariantForCandidate(position, value);
          const available = Boolean(reachable && candidate && candidate.available);
          const stateLabel = reachable && candidate?.available === false ? ' — Sold Out' : (!reachable ? ' — Unavailable' : '');
          option.disabled = !available;
          option.textContent = `${value}${stateLabel}`;
        });
      });

      this.setText(root, '[data-theme-quick-view-price]', variant?.price_formatted || '');
      this.window.ThemeLowStock?.render(root.querySelector('[data-theme-low-stock]'), variant, this.lowStockStale);
      const compare = root.querySelector('[data-theme-quick-view-compare-price]');
      const hasSale = Boolean(variant && variant.compare_at_price != null && Number(variant.compare_at_price) > Number(variant.price));
      if (compare) {
        compare.textContent = hasSale ? variant.compare_at_price_formatted || '' : '';
        compare.hidden = !hasSale;
      }

      const badge = root.querySelector('[data-theme-quick-view-badge]');
      const productAvailability = state.root.getAttribute('data-theme-quick-view-product-available');
      const familyAvailability = state.root.getAttribute('data-theme-quick-view-family-has-available');
      const productAvailable = productAvailability == null ? state.variants.some((candidate) => candidate.available) : productAvailability === 'true';
      const familyHasAvailable = familyAvailability == null ? productAvailable : familyAvailability === 'true';
      if (badge) {
        const availabilityLabel = productAvailable ? '' : (familyHasAvailable ? 'Color sold out' : 'Sold out');
        badge.textContent = availabilityLabel;
        badge.hidden = !availabilityLabel;
        badge.classList.toggle('theme-availability-badge--color-sold-out', availabilityLabel === 'Color sold out');
        badge.classList.toggle('theme-availability-badge--sold-out', availabilityLabel === 'Sold out');
      }

      // Measure the intrinsic desktop envelope before the normal-flow note is revealed.
      this.preserveAvailabilityNoteEnvelope(root);
      this.window.ThemeAvailabilityNote?.render(root, state.variants, state.selection, variant, familyHasAvailable);

      if (state.gallery) {
        let galleryTargetIndex = null;
        if (variantChanged) {
          this.cancelGalleryGesture({ immediate: true });
          this.galleryGesture = null;
          const variantImageIndex = this.galleryIndexForVariant(state, variant);
          if (variantImageIndex >= 0) galleryTargetIndex = variantImageIndex;
        }
        this.updateGallery(root, state, {
          targetIndex: galleryTargetIndex,
          source: variantChanged ? 'variant' : 'sync'
        });
      } else {
        this.updateMedia(root, variant?.media || state.fallbackMedia);
      }
      this.updatePurchase(root, variant);
      this.updateFullLink(root, variant);
      if (state.userInteracted) {
        const productForm = root.querySelector('[data-theme-quick-view-product-form]');
        if (productForm && typeof productForm.handleErrorMessage === 'function') productForm.handleErrorMessage();
        if (productForm) productForm.error = false;
      }
      root.setAttribute('data-theme-quick-view-current-variant-id', variant ? String(variant.id) : '');
      this.scheduleLayoutFit();
    }

    galleryIndexForVariant(state, variant) {
      if (!state?.gallery || !variant) return -1;
      const imageId = variant.featured_image_id ?? variant.media?.id;
      if (imageId == null) return -1;
      return state.gallery.images.findIndex((image) => image.id === String(imageId));
    }

    galleryViewport(frame) {
      return frame?.querySelector?.('[data-theme-quick-view-gallery-viewport]') || frame || null;
    }

    gallerySlide(viewport, role) {
      if (!viewport) return null;
      const direct = viewport.querySelector?.(`[data-theme-quick-view-gallery-slide][data-gallery-slide-role="${role}"]`);
      if (direct) return direct;
      return Array.from(viewport.children || []).find((child) =>
        child?.getAttribute?.('data-theme-quick-view-gallery-slide') !== null &&
        child.getAttribute('data-gallery-slide-role') === role
      ) || null;
    }

    createGallerySlide(viewport, role) {
      const slide = this.document.createElement('div');
      slide.className = 'theme-quick-view__media-slide';
      slide.setAttribute('data-theme-quick-view-gallery-slide', '');
      slide.setAttribute('data-gallery-slide-role', role);
      slide.setAttribute('aria-hidden', role === 'current' ? 'false' : 'true');
      viewport.append?.(slide);
      return slide;
    }

    ensureGalleryCurrentSlide(frame) {
      const viewport = this.galleryViewport(frame);
      let slide = this.gallerySlide(viewport, 'current');
      if (slide || viewport === frame) return slide;
      slide = this.createGallerySlide(viewport, 'current');
      return slide;
    }

    updateGalleryControls(root, state) {
      const gallery = state?.gallery;
      if (!gallery) return;
      const images = gallery.images;
      const index = images.length ? Math.max(0, Math.min(gallery.currentIndex, images.length - 1)) : 0;
      const previous = root.querySelector('[data-theme-quick-view-gallery-previous]');
      const next = root.querySelector('[data-theme-quick-view-gallery-next]');
      const enlarge = root.querySelector('[data-theme-quick-view-enlarge]');
      const title = root.querySelector('[data-theme-quick-view-title]')?.textContent?.trim() || 'product';
      const hasImages = images.length > 0;
      const hasNavigation = images.length > 1;
      for (const [control, label] of [[previous, 'Previous'], [next, 'Next']]) {
        if (!control) continue;
        control.hidden = !hasImages;
        control.disabled = !hasNavigation;
        control.setAttribute('aria-disabled', control.disabled ? 'true' : 'false');
        control.setAttribute('aria-label', `${label} image of ${title}`);
      }
      if (enlarge) this.fitQuickViewEnlarge(root);
      this.scheduleLayoutFit();
    }

    openLightbox() {
      const state = this.productState;
      const images = state?.gallery?.images || [];
      const lightbox = state?.root?.querySelector?.('[data-theme-quick-view-lightbox]');
      if (!this.isOpen() || !images.length || !lightbox || typeof lightbox.showModal !== 'function') return false;
      this.cancelGalleryGesture({ immediate: true });
      this.lightbox = lightbox;
      this.lightboxIndex = Math.max(0, Math.min(state.gallery.currentIndex, images.length - 1));
      this.lightboxZoom = 1;
      this.removeFocusTrap();
      try {
        lightbox.showModal();
      } catch (error) {
        this.lightbox = null;
        this.refreshFocusTrapKeepingFocus();
        return false;
      }
      this.lightboxViewer = new this.window.ThemeMediaViewer({
        viewport: lightbox.querySelector('[data-theme-quick-view-lightbox-viewport]'),
        image: lightbox.querySelector('[data-theme-quick-view-lightbox-image]'),
        getAdjacent: (direction) => {
          if (images.length < 2) return null;
          const image = images[(this.lightboxIndex + direction + images.length) % images.length];
          return { src: image.zoomSrc || image.src, previewSrc: image.src };
        },
        onNavigate: (direction, source) => this.commitLightboxNavigation(direction, source),
        onUpgrade: (source, previewSrc) => {
          const viewerImage = this.lightbox?.querySelector('[data-theme-quick-view-lightbox-image]');
          if (this.lightbox?.open && viewerImage?.getAttribute('src') === previewSrc) viewerImage.src = source;
        },
        onZoomChange: (zoom) => {
          this.lightboxZoom = zoom;
          const reset = lightbox.querySelector('[data-theme-quick-view-zoom-reset]');
          if (reset) reset.textContent = `${Math.round(zoom * 100)}%`;
        }
      });
      this.renderLightbox();
      this.focusWithoutScroll(lightbox.querySelector('[data-theme-quick-view-lightbox-close]'));
      return true;
    }

    renderLightbox(source = null) {
      const images = this.productState?.gallery?.images || [];
      const image = images[this.lightboxIndex];
      const lightbox = this.lightbox;
      if (!image || !lightbox?.open) return;
      const viewerImage = lightbox.querySelector('[data-theme-quick-view-lightbox-image]');
      if (viewerImage) {
        viewerImage.src = source || image.zoomSrc || image.src;
        viewerImage.alt = image.alt || this.galleryAlt(this.productState.root, this.lightboxIndex, images.length);
        viewerImage.width = image.width || 2000;
        viewerImage.height = image.height || 2500;
      }
      const count = lightbox.querySelector('[data-theme-quick-view-lightbox-count]');
      if (count) count.textContent = `${this.lightboxIndex + 1} of ${images.length}`;
      for (const selector of ['[data-theme-quick-view-lightbox-previous]', '[data-theme-quick-view-lightbox-next]']) {
        const button = lightbox.querySelector(selector);
        if (button) button.disabled = images.length < 2;
      }
      this.setLightboxZoom(1);
      this.lightboxViewer?.prime();
    }

    navigateLightbox(delta) {
      this.lightboxViewer?.navigate(delta);
    }

    commitLightboxNavigation(delta, source = null) {
      const length = this.productState?.gallery?.images?.length || 0;
      if (!this.lightbox?.open || length < 2) return;
      this.lightboxIndex = (this.lightboxIndex + delta + length) % length;
      this.renderLightbox(source);
    }

    setLightboxZoom(value) {
      this.lightboxViewer?.setZoom(value);
    }

    applyLightboxTransform() {
      this.lightboxViewer?.refresh();
    }

    closeLightbox({ restoreFocus = true } = {}) {
      const lightbox = this.lightbox;
      if (!lightbox) return;
      const state = this.productState;
      const index = this.lightboxIndex;
      if (lightbox.open) lightbox.close();
      this.lightboxViewer?.destroy();
      this.lightboxViewer = null;
      this.lightbox = null;
      this.lightboxZoom = 1;
      if (!restoreFocus || !this.isOpen()) return;
      if (state?.gallery && index !== state.gallery.currentIndex) {
        state.gallery.currentIndex = index;
        state.gallery.manualSelection = true;
        this.updateGallery(state.root, state, { targetIndex: index, source: 'lightbox' });
      }
      const enlarge = state?.root?.querySelector?.('[data-theme-quick-view-enlarge]');
      this.focusWithoutScroll(enlarge || this.dialog.querySelector('[data-theme-quick-view-close]'));
      this.refreshFocusTrapKeepingFocus();
    }

    updateGallery(root, state, { announceReady = false, targetIndex = null, source = 'sync' } = {}) {
      const gallery = state?.gallery;
      const frame = root?.querySelector('[data-theme-quick-view-gallery-frame]');
      if (!gallery || !frame) return;
      const images = gallery.images;
      const index = images.length ? Math.max(0, Math.min(gallery.currentIndex, images.length - 1)) : 0;
      gallery.currentIndex = index;

      const hasImages = images.length > 0;
      this.updateGalleryControls(root, state);

      if (!hasImages) {
        const currentImage = gallery.currentImage;
        this.cancelPendingGalleryImage(state, frame);
        gallery.renderedImageId = null;
        gallery.brokenImageId = null;
        gallery.currentImage = null;
        const imagesInFrame = typeof frame.querySelectorAll === 'function' ?
          Array.from(frame.querySelectorAll('[data-theme-quick-view-gallery-image]')) : [];
        const fallbackImage = currentImage || frame.querySelector('[data-theme-quick-view-gallery-image]');
        if (fallbackImage && !imagesInFrame.includes(fallbackImage)) imagesInFrame.push(fallbackImage);
        imagesInFrame.forEach((image) => this.removeGalleryImage(image));
        const placeholder = this.ensureGalleryPlaceholder(frame);
        placeholder.hidden = false;
        placeholder.textContent = 'Product image unavailable';
        placeholder.setAttribute('aria-label', 'Product image unavailable');
        this.clearGalleryPendingVisuals(frame);
        return;
      }

      const requestedIndex = targetIndex == null ? index : Math.max(0, Math.min(targetIndex, images.length - 1));
      this.renderGalleryImage(root, state, images[requestedIndex], requestedIndex, { announceReady, source });
      this.preloadGalleryNeighbors(state);
    }

    renderGalleryImage(root, state, imageData, index, { announceReady = false, source = 'sync' } = {}) {
      const gallery = state.gallery;
      const frame = root.querySelector('[data-theme-quick-view-gallery-frame]');
      if (!frame || !imageData) return;
      const frameProductId = frame.getAttribute?.('data-theme-quick-view-gallery-product-id');
      if (frameProductId && frameProductId !== gallery.productId) {
        const staleImages = typeof frame.querySelectorAll === 'function' ?
          Array.from(frame.querySelectorAll('[data-theme-quick-view-gallery-image]')) :
          Array.from(frame.children || []).filter((child) =>
            typeof child?.getAttribute === 'function' && child.getAttribute('data-theme-quick-view-gallery-image') !== null
          );
        if (gallery.currentImage && !staleImages.includes(gallery.currentImage)) staleImages.push(gallery.currentImage);
        if (gallery.pendingImage?.image && !staleImages.includes(gallery.pendingImage.image)) staleImages.push(gallery.pendingImage.image);
        staleImages.forEach((image) => this.removeGalleryImage(image));
        gallery.currentImage = null;
        gallery.pendingImage = null;
        gallery.renderedImageId = null;
        gallery.brokenImageId = null;
        this.clearGalleryPendingVisuals(frame);
      }
      frame.setAttribute('data-theme-quick-view-gallery-product-id', gallery.productId);
      const placeholder = this.ensureGalleryPlaceholder(frame);
      const currentImage = this.resolveCurrentGalleryImage(frame, gallery);
      const pending = gallery.pendingImage;
      const pendingMatches = pending && pending.imageData.id === imageData.id && pending.index === index;
      const currentMatches = currentImage && currentImage.getAttribute('data-theme-quick-view-gallery-image-id') === imageData.id;

      if (pendingMatches) return;

      if (currentMatches) {
        // Returning to the retained current image supersedes any older
        // candidate that was still pending. Remove that candidate before
        // clearing the pending paint state so late events cannot revive it.
        if (pending) this.cancelPendingGalleryImage(state, frame);
        gallery.currentImage = currentImage;
        gallery.renderedImageId = imageData.id;
        if (source === 'variant') gallery.manualSelection = false;
        currentImage.setAttribute('data-theme-quick-view-gallery-image-role', 'current');
        currentImage.__themeQuickViewBroken = Boolean(gallery.brokenImageId === imageData.id);
        if (currentImage.__themeQuickViewBroken) {
          currentImage.hidden = true;
          placeholder.hidden = false;
          this.clearGalleryPendingVisuals(frame);
          return;
        }
        this.configureGalleryImage(currentImage, imageData, root, index, gallery.images.length, { preserveSource: true });
        if (this.isGalleryImageReady(currentImage)) {
          currentImage.__themeQuickViewReady = true;
          currentImage.hidden = false;
          placeholder.hidden = true;
          gallery.brokenImageId = null;
          this.clearGalleryPendingVisuals(frame);
          return;
        }
        this.beginGalleryImageLoad(root, state, frame, imageData, index, currentImage, { announceReady, source });
        return;
      }

      this.cancelPendingGalleryImage(state, frame);
      const image = this.document.createElement('img');
      image.className = 'theme-quick-view__media-image';
      image.setAttribute('data-theme-quick-view-gallery-image', '');
      image.setAttribute('data-theme-quick-view-gallery-image-id', imageData.id);
      const viewport = this.galleryViewport(frame);
      let pendingSlide = viewport !== frame ? this.createGallerySlide(viewport, 'incoming') : null;
      (pendingSlide || viewport).append?.(image);
      image.__themeQuickViewGallerySlide = pendingSlide;
      this.beginGalleryImageLoad(root, state, frame, imageData, index, image, { announceReady, source });
    }

    resolveCurrentGalleryImage(frame, gallery) {
      const stored = gallery?.currentImage;
      if (stored && (stored.parentNode === frame || frame.contains?.(stored))) return stored;
      const marked = frame.querySelector('[data-theme-quick-view-gallery-image-role="current"]');
      if (marked) {
        gallery.currentImage = marked;
        return marked;
      }
      const image = frame.querySelector('[data-theme-quick-view-gallery-image]');
      if (image && !gallery?.pendingImage) {
        gallery.currentImage = image;
        return image;
      }
      return null;
    }

    configureGalleryImage(image, imageData, root, index, count, { preserveSource = false, setSource = true } = {}) {
      image.alt = String(imageData.alt || this.galleryAlt(root, index, count));
      image.width = Number(imageData.width) || image.width || 1;
      image.height = Number(imageData.height) || image.height || 1;
      image.loading = 'eager';
      image.decoding = 'async';
      image.fetchPriority = 'high';
      image.draggable = false;
      const srcset = imageData.srcset.length ? imageData.srcset.map((candidate) => `${candidate.src} ${candidate.width}w`).join(', ') : '';
      if (srcset) image.srcset = srcset;
      else if (!preserveSource && typeof image.removeAttribute === 'function') image.removeAttribute('srcset');
      image.sizes = GALLERY_IMAGE_SIZES;
      if (setSource && (!preserveSource || !image.src)) image.src = imageData.src;
    }

    isGalleryImageReady(image) {
      if (!image) return false;
      if (image.__themeQuickViewReady === true) return true;
      if (image.complete !== true) return false;
      if (!('naturalWidth' in image)) return true;
      return Number(image.naturalWidth) > 0;
    }

    beginGalleryImageLoad(root, state, frame, imageData, index, image, { announceReady = false, source = 'sync' } = {}) {
      const gallery = state.gallery;
      const placeholder = this.ensureGalleryPlaceholder(frame);
      const imageToken = ++gallery.imageToken;
      const pending = {
        state,
        frame,
        image,
        imageData,
        index,
        token: imageToken,
        announceReady,
        source,
        readyStarted: false,
        slide: image.__themeQuickViewGallerySlide || image.closest?.('[data-theme-quick-view-gallery-slide]') || null
      };
      gallery.pendingImage = pending;
      image.__themeQuickViewGalleryToken = imageToken;
      image.__themeQuickViewBroken = false;
      image.setAttribute('data-theme-quick-view-gallery-image-role', 'pending');
      pending.slide?.setAttribute?.('aria-hidden', 'true');
      image.hidden = true;
      this.configureGalleryImage(image, imageData, root, index, gallery.images.length, { setSource: false });
      this.setGalleryPendingVisuals(frame);

      const imageIsCurrent = () => this.isCurrentGalleryImage(state, image, index, imageToken);
      const settleReady = () => {
        if (!imageIsCurrent() || pending.readyStarted) return;
        pending.readyStarted = true;
        const decode = typeof image.decode === 'function' ?
          Promise.resolve().then(() => image.decode()) : Promise.resolve();
        decode.then(() => this.commitGalleryImage(pending)).catch(() => this.failGalleryImage(pending));
      };
      image.addEventListener('load', settleReady);
      image.addEventListener('error', () => {
        if (!imageIsCurrent()) return;
        this.failGalleryImage(pending);
      });

      if (this.isGalleryImageReady(image)) settleReady();
      if (image.src !== imageData.src) image.src = imageData.src;
      if (this.isGalleryImageReady(image)) settleReady();

      const retained = gallery.currentImage && gallery.currentImage !== image ? gallery.currentImage : null;
      const retainedReady = Boolean(retained && !retained.hidden && this.isGalleryImageReady(retained));
      if (retained && !retainedReady) retained.hidden = true;
      // A pending first image leaves the media frame neutral. The unavailable
      // message is reserved for a confirmed load error or a zero-image
      // product, so a slow request is never presented as a failure.
      if (!retainedReady) placeholder.hidden = true;
    }

    commitGalleryImage(pending) {
      const { state, frame, image, imageData, index } = pending;
      if (!this.isCurrentGalleryImage(state, image, index, pending.token)) return false;
      const gallery = state.gallery;
      const previous = gallery.currentImage && gallery.currentImage !== image ? gallery.currentImage : null;
      image.__themeQuickViewReady = true;
      image.__themeQuickViewBroken = false;
      image.hidden = false;
      image.setAttribute('data-theme-quick-view-gallery-image-role', 'current');
      if (pending.slide) {
        pending.slide.setAttribute('data-gallery-slide-role', 'current');
        pending.slide.setAttribute('aria-hidden', 'false');
      }
      gallery.currentImage = image;
      gallery.pendingImage = null;
      gallery.currentIndex = index;
      if (pending.source === 'variant') gallery.manualSelection = false;
      gallery.renderedImageId = imageData.id;
      gallery.brokenImageId = null;
      const placeholder = this.ensureGalleryPlaceholder(frame);
      placeholder.hidden = true;
      this.clearGalleryPendingVisuals(frame);
      if (previous) {
        const previousSlide = previous.closest?.('[data-theme-quick-view-gallery-slide]');
        if (previousSlide && previousSlide !== pending.slide) previousSlide.remove?.();
        else this.removeGalleryImage(previous);
      }
      this.updateGalleryControls(state.root, state);
      this.preloadGalleryNeighbors(state);
      if (pending.announceReady) this.announceGallery(state, `Image ${index + 1} of ${gallery.images.length} ready`);
      return true;
    }

    failGalleryImage(pending) {
      const { state, frame, image, imageData, index } = pending;
      if (!this.isCurrentGalleryImage(state, image, index, pending.token)) return false;
      const gallery = state.gallery;
      const previous = gallery.currentImage && gallery.currentImage !== image ? gallery.currentImage : null;
      image.__themeQuickViewBroken = true;
      image.hidden = true;
      const failedSlide = pending.slide;
      this.removeGalleryImage(image);
      failedSlide?.remove?.();
      gallery.pendingImage = null;
      if (previous) {
        gallery.currentImage = previous;
        gallery.renderedImageId = previous.getAttribute?.('data-theme-quick-view-gallery-image-id');
      } else {
        gallery.currentImage = null;
        gallery.renderedImageId = imageData.id;
      }
      gallery.brokenImageId = imageData.id;
      const placeholder = this.ensureGalleryPlaceholder(frame);
      placeholder.hidden = Boolean(previous);
      if (!previous) {
        placeholder.textContent = 'Product image unavailable';
        placeholder.setAttribute('aria-label', 'Product image unavailable');
      }
      this.clearGalleryPendingVisuals(frame);
      this.updateGalleryControls(state.root, state);
      if (!previous) this.announceGallery(state, `Image ${index + 1} of ${gallery.images.length} unavailable`);
      return true;
    }

    removeGalleryImage(image) {
      if (!image) return;
      if (typeof image.remove === 'function') image.remove();
      else if (image.parentNode && typeof image.parentNode.removeChild === 'function') image.parentNode.removeChild(image);
    }

    cancelPendingGalleryImage(state, frame) {
      const gallery = state?.gallery;
      const pending = gallery?.pendingImage;
      if (!pending) return;
      gallery.pendingImage = null;
      const wasCurrent = pending.image === gallery.currentImage;
      this.removeGalleryImage(pending.image);
      pending.slide?.remove?.();
      if (wasCurrent) gallery.currentImage = null;
      this.clearGalleryPendingVisuals(frame || pending.frame);
      if (!gallery.currentImage) {
        const placeholder = this.ensureGalleryPlaceholder(frame || pending.frame);
        placeholder.hidden = false;
      }
    }

    ensureGallerySpinner(frame) {
      if (frame.__themeQuickViewGallerySpinner) return frame.__themeQuickViewGallerySpinner;
      let spinner = frame.querySelector('[data-theme-quick-view-gallery-spinner]');
      if (!spinner) {
        spinner = this.document.createElement('span');
        spinner.className = 'theme-quick-view__gallery-spinner';
        spinner.setAttribute('data-theme-quick-view-gallery-spinner', '');
        spinner.setAttribute('aria-hidden', 'true');
        this.galleryViewport(frame)?.append?.(spinner);
      }
      frame.__themeQuickViewGallerySpinner = spinner;
      return spinner;
    }

    setGalleryPendingVisuals(frame) {
      if (!frame) return;
      frame.setAttribute('aria-busy', 'true');
      const gallery = frame.closest?.('[data-theme-quick-view-gallery]');
      if (gallery && gallery !== frame) gallery.setAttribute('aria-busy', 'true');
      this.ensureGallerySpinner(frame).hidden = false;
    }

    clearGalleryPendingVisuals(frame) {
      if (!frame) return;
      if (typeof frame.removeAttribute === 'function') frame.removeAttribute('aria-busy');
      const gallery = frame.closest?.('[data-theme-quick-view-gallery]');
      if (gallery && gallery !== frame && typeof gallery.removeAttribute === 'function') gallery.removeAttribute('aria-busy');
      const spinner = frame.__themeQuickViewGallerySpinner || frame.querySelector('[data-theme-quick-view-gallery-spinner]');
      if (spinner) spinner.hidden = true;
    }

    galleryAlt(root, index, count) {
      const title = root.querySelector('[data-theme-quick-view-title]')?.textContent?.trim() || 'Product';
      return `${title}, image ${index + 1} of ${count}`;
    }

    ensureGalleryPlaceholder(frame) {
      let placeholder = frame.querySelector('[data-theme-quick-view-gallery-placeholder]');
      if (!placeholder) {
        placeholder = this.document.createElement('span');
        placeholder.className = 'theme-quick-view__media-placeholder';
        placeholder.setAttribute('data-theme-quick-view-media-placeholder', '');
        placeholder.setAttribute('data-theme-quick-view-gallery-placeholder', '');
        placeholder.setAttribute('role', 'img');
        placeholder.setAttribute('aria-label', 'Product image unavailable');
        placeholder.textContent = 'Product image unavailable';
        const viewport = this.galleryViewport(frame);
        const currentSlide = this.gallerySlide(viewport, 'current') || (viewport !== frame ? this.createGallerySlide(viewport, 'current') : viewport);
        currentSlide?.append?.(placeholder);
      }
      return placeholder;
    }

    isCurrentGalleryImage(state, image, index, imageToken) {
      const gallery = state?.gallery;
      const pending = gallery?.pendingImage;
      return Boolean(
        this.isOpen() &&
        this.productState === state &&
        gallery &&
        gallery.productId === state.root.getAttribute('data-theme-quick-view-product-id') &&
        pending &&
        pending.index === index &&
        pending.image === image &&
        pending.token === imageToken &&
        image.__themeQuickViewGalleryToken === imageToken
      );
    }

    galleryWarmupKey(state, imageData) {
      return JSON.stringify([
        String(state?.gallery?.productId || ''),
        String(imageData?.id || ''),
        String(imageData?.src || '')
      ]);
    }

    clearGalleryWarmups() {
      this.galleryWarmupKeys.clear();
      this.galleryWarmupImages.clear();
      this.galleryWarmupSequence = 0;
    }

    evictGalleryWarmups() {
      if (this.galleryWarmupImages.size <= GALLERY_RESOURCE_LIMIT) return;
      const protectedKeys = new Set();
      const interaction = this.galleryInteraction;
      if (interaction?.resource?.key) protectedKeys.add(interaction.resource.key);
      const current = this.productState?.gallery;
      if (current?.images?.length) protectedKeys.add(this.galleryWarmupKey(this.productState, current.images[current.currentIndex]));
      const candidates = Array.from(this.galleryWarmupImages.entries())
        .filter(([key]) => !protectedKeys.has(key))
        .sort((left, right) => (left[1].lastUsed || 0) - (right[1].lastUsed || 0));
      while (this.galleryWarmupImages.size > GALLERY_RESOURCE_LIMIT && candidates.length) {
        const [key] = candidates.shift();
        this.galleryWarmupImages.delete(key);
        this.galleryWarmupKeys.delete(key);
      }
    }

    prepareGalleryResource(state, imageData, { explicit = false } = {}) {
      const key = this.galleryWarmupKey(state, imageData);
      const existing = this.galleryWarmupImages.get(key);
      if (existing) {
        existing.lastUsed = ++this.galleryWarmupSequence;
        return existing;
      }
      const record = {
        key,
        stateProductId: state?.gallery?.productId || '',
        imageData,
        status: 'loading',
        image: null,
        promise: null,
        lastUsed: ++this.galleryWarmupSequence,
        explicit
      };
      this.galleryWarmupKeys.add(key);
      this.galleryWarmupImages.set(key, record);
      if (typeof this.window.Image !== 'function') {
        record.status = 'decoded';
        record.promise = Promise.resolve(record);
        this.evictGalleryWarmups();
        return record;
      }
      const candidate = new this.window.Image();
      record.image = candidate;
      candidate.decoding = 'async';
      candidate.loading = 'eager';
      candidate.fetchPriority = explicit ? 'high' : 'low';
      if (Array.isArray(imageData.srcset) && imageData.srcset.length) candidate.srcset = imageData.srcset.map((item) => `${item.src} ${item.width}w`).join(', ');
      candidate.sizes = GALLERY_IMAGE_SIZES;
      record.promise = new Promise((resolve) => {
        let settled = false;
        const finish = (status) => {
          if (settled) return;
          settled = true;
          record.status = status;
          record.lastUsed = ++this.galleryWarmupSequence;
          this.evictGalleryWarmups();
          resolve(record);
        };
        const ready = () => {
          const decode = typeof candidate.decode === 'function' ? Promise.resolve().then(() => candidate.decode()) : Promise.resolve();
          decode.then(() => finish('decoded')).catch(() => finish('failed'));
        };
        if (typeof candidate.addEventListener === 'function') {
          candidate.addEventListener('load', ready, { once: true });
          candidate.addEventListener('error', () => finish('failed'), { once: true });
        } else {
          candidate.onload = ready;
          candidate.onerror = () => finish('failed');
        }
        candidate.src = imageData.src;
        if (this.isGalleryImageReady(candidate)) ready();
      });
      this.evictGalleryWarmups();
      return record;
    }

    preloadGalleryNeighbors(state) {
      const gallery = state?.gallery;
      const images = gallery?.images || [];
      if (!this.isOpen() || images.length < 2 || typeof this.window.Image !== 'function') return;
      if (this.window.navigator?.connection?.saveData === true) return;
      const currentIndex = Math.max(0, Math.min(gallery.currentIndex, images.length - 1));
      const neighborIndexes = new Set([
        (currentIndex - 1 + images.length) % images.length,
        (currentIndex + 1) % images.length
      ]);
      for (const index of neighborIndexes) {
        const imageData = images[index];
        this.prepareGalleryResource(state, imageData);
      }
    }

    createIncomingGallerySlide(interaction) {
      const { frame, state, imageData, targetIndex, resource } = interaction;
      const viewport = this.galleryViewport(frame);
      if (!viewport || viewport === frame) return null;
      const oldIncoming = this.gallerySlide(viewport, 'incoming');
      if (oldIncoming) oldIncoming.remove?.();
      const slide = this.createGallerySlide(viewport, 'incoming');
      slide.setAttribute('aria-hidden', 'true');
      interaction.incomingSlide = slide;
      if (resource.status === 'failed') {
        const placeholder = this.document.createElement('span');
        placeholder.className = 'theme-quick-view__media-placeholder';
        placeholder.setAttribute('data-theme-quick-view-gallery-placeholder', '');
        placeholder.setAttribute('role', 'img');
        placeholder.setAttribute('aria-label', 'Product image unavailable');
        placeholder.textContent = 'Product image unavailable';
        slide.append?.(placeholder);
        interaction.failed = true;
      } else {
        const image = this.document.createElement('img');
        image.className = 'theme-quick-view__media-image';
        image.setAttribute('data-theme-quick-view-gallery-image', '');
        image.setAttribute('data-theme-quick-view-gallery-image-id', imageData.id);
        image.setAttribute('data-theme-quick-view-gallery-image-role', 'incoming');
        this.configureGalleryImage(image, imageData, state.root, targetIndex, state.gallery.images.length);
        image.__themeQuickViewReady = true;
        slide.append?.(image);
        interaction.incomingImage = image;
      }
      interaction.ready = true;
      frame.style?.setProperty?.('--theme-quick-view-gallery-incoming-start', interaction.direction > 0 ? '100%' : '-100%');
      frame.classList?.remove?.('is-gallery-waiting');
      this.clearGalleryPendingVisuals(frame);
      return slide;
    }

    prepareGalleryInteraction(direction, source = 'gesture') {
      const state = this.productState;
      if (!state?.gallery?.images || state.gallery.images.length < 2 || !Number.isFinite(direction)) return false;
      if (this.galleryInteraction?.phase === 'settling') this.finishGalleryTransition(this.galleryInteraction.token, 'superseded');
      const gallery = state.gallery;
      const currentIndex = Math.max(0, Math.min(gallery.currentIndex, gallery.images.length - 1));
      const normalizedDirection = direction < 0 ? -1 : 1;
      const targetIndex = (currentIndex + normalizedDirection + gallery.images.length) % gallery.images.length;
      const existing = this.galleryInteraction;
      if (existing?.phase === 'waiting-image' && existing.targetIndex === targetIndex) return existing;
      if (existing) this.cancelGalleryGesture({ immediate: true });
      const frame = state.root.querySelector('[data-theme-quick-view-gallery-frame]');
      const viewport = this.galleryViewport(frame);
      if (!frame || !viewport || viewport === frame) return false;
      const imageData = gallery.images[targetIndex];
      const resource = this.prepareGalleryResource(state, imageData, { explicit: true });
      const interaction = {
        token: ++this.galleryInteractionSerial,
        phase: resource.status === 'loading' ? 'waiting-image' : 'ready',
        state,
        frame,
        viewport,
        source,
        direction: normalizedDirection,
        targetIndex,
        imageData,
        resource,
        ready: false,
        released: source !== 'gesture',
        qualified: source !== 'gesture',
        frameWidth: frame.getBoundingClientRect?.().width || frame.clientWidth || 1,
        latestOffset: 0,
        transitionListener: null,
        transitionTimer: null,
        finished: false
      };
      this.galleryInteraction = interaction;
      if (resource.status === 'decoded' || resource.status === 'failed') {
        this.createIncomingGallerySlide(interaction);
        if (source !== 'gesture') this.settleGalleryInteraction(interaction, true);
      } else {
        frame.classList?.add?.('is-gallery-waiting');
        this.setGalleryPendingVisuals(frame);
        resource.promise.then((settled) => {
          if (this.galleryInteraction !== interaction || interaction.token !== this.galleryInteractionSerial || interaction.finished) return;
          interaction.phase = 'ready';
          interaction.resource = settled;
          this.createIncomingGallerySlide(interaction);
          if (this.galleryGesture?.lockedOwner === 'gallery' && !interaction.released) {
            this.galleryGesture.startX = this.galleryGesture.lastX;
            interaction.latestOffset = 0;
          }
          if (interaction.released && interaction.qualified) this.settleGalleryInteraction(interaction, true);
        });
      }
      return interaction;
    }

    beginGalleryDrag(direction, frame) {
      const interaction = this.prepareGalleryInteraction(direction, 'gesture');
      if (!interaction || (frame && interaction.frame !== frame)) return false;
      interaction.frame.classList?.add?.('is-gallery-dragging');
      return true;
    }

    updateGalleryDrag(offset) {
      const interaction = this.galleryInteraction;
      if (!interaction || !interaction.ready || interaction.phase === 'settling') return false;
      const allowed = interaction.direction > 0 ? Math.min(0, offset) : Math.max(0, offset);
      const bounded = Math.max(-interaction.frameWidth, Math.min(interaction.frameWidth, allowed));
      interaction.latestOffset = bounded;
      this.galleryPendingOffset = bounded;
      if (this.galleryAnimationFrame != null) return true;
      const render = () => {
        this.galleryAnimationFrame = null;
        interaction.frame.style?.setProperty?.('--theme-quick-view-gallery-offset', `${this.galleryPendingOffset}px`);
      };
      if (typeof this.window.requestAnimationFrame === 'function') this.galleryAnimationFrame = this.window.requestAnimationFrame(render);
      else render();
      return true;
    }

    finishGalleryDrag(qualified) {
      const interaction = this.galleryInteraction;
      if (!interaction) return false;
      interaction.released = true;
      interaction.qualified = Boolean(qualified);
      if (qualified) {
        if (interaction.ready) this.settleGalleryInteraction(interaction, true);
      } else if (interaction.ready && interaction.latestOffset) {
        this.settleGalleryInteraction(interaction, false);
      } else {
        this.cancelGalleryGesture({ immediate: true });
      }
      return Boolean(qualified);
    }

    settleGalleryInteraction(interaction, complete) {
      if (!interaction || this.galleryInteraction !== interaction || interaction.finished) return false;
      interaction.phase = 'settling';
      interaction.complete = complete;
      interaction.frame.classList?.remove?.('is-gallery-dragging', 'is-gallery-waiting');
      if (this.prefersReducedMotion()) return this.finishGalleryTransition(interaction.token, 'reduced-motion');
      interaction.frame.classList?.add?.('is-gallery-settling');
      const expectedSlide = interaction.incomingSlide;
      interaction.transitionListener = (event) => {
        if (
          this.galleryInteraction !== interaction ||
          interaction.token !== this.galleryInteractionSerial ||
          this.productState !== interaction.state ||
          (interaction.state.root?.isConnected !== undefined && !interaction.state.root.isConnected) ||
          event.target !== expectedSlide ||
          event.propertyName !== 'transform'
        ) return;
        this.finishGalleryTransition(interaction.token, 'transitionend');
      };
      expectedSlide?.addEventListener?.('transitionend', interaction.transitionListener);
      interaction.transitionTimer = this.window.setTimeout?.(() => this.finishGalleryTransition(interaction.token, 'timeout'), GALLERY_TRANSITION_TIMEOUT) ?? null;
      const finalOffset = complete ? (interaction.direction > 0 ? -interaction.frameWidth : interaction.frameWidth) : 0;
      if (this.galleryAnimationFrame != null) this.window.cancelAnimationFrame?.(this.galleryAnimationFrame);
      const beginSettle = () => {
        this.galleryAnimationFrame = null;
        if (this.galleryInteraction === interaction && !interaction.finished) interaction.frame.style?.setProperty?.('--theme-quick-view-gallery-offset', `${finalOffset}px`);
      };
      if (typeof this.window.requestAnimationFrame === 'function') this.galleryAnimationFrame = this.window.requestAnimationFrame(beginSettle);
      else beginSettle();
      return true;
    }

    finishGalleryTransition(token, reason = 'complete') {
      const interaction = this.galleryInteraction;
      if (!interaction || interaction.token !== token || interaction.finished) return false;
      interaction.finished = true;
      interaction.incomingSlide?.removeEventListener?.('transitionend', interaction.transitionListener);
      if (interaction.transitionTimer != null) this.window.clearTimeout?.(interaction.transitionTimer);
      if (this.galleryAnimationFrame != null) this.window.cancelAnimationFrame?.(this.galleryAnimationFrame);
      this.galleryAnimationFrame = null;
      const { frame, state } = interaction;
      frame.classList?.remove?.('is-gallery-dragging', 'is-gallery-waiting', 'is-gallery-settling');
      frame.style?.setProperty?.('--theme-quick-view-gallery-offset', '0px');
      if (interaction.complete && interaction.incomingSlide) {
        const currentSlide = this.gallerySlide(interaction.viewport, 'current');
        if (currentSlide && currentSlide !== interaction.incomingSlide) currentSlide.remove?.();
        interaction.incomingSlide.setAttribute('data-gallery-slide-role', 'current');
        interaction.incomingSlide.setAttribute('aria-hidden', 'false');
        interaction.incomingImage?.setAttribute?.('data-theme-quick-view-gallery-image-role', 'current');
        state.gallery.currentIndex = interaction.targetIndex;
        state.gallery.manualSelection = interaction.source !== 'variant';
        state.gallery.currentImage = interaction.incomingImage || null;
        state.gallery.renderedImageId = interaction.imageData.id;
        state.gallery.brokenImageId = interaction.failed ? interaction.imageData.id : null;
        this.updateGalleryControls(state.root, state);
        this.announceGallery(state, interaction.failed ? `Image ${interaction.targetIndex + 1} of ${state.gallery.images.length} unavailable` : `Image ${interaction.targetIndex + 1} of ${state.gallery.images.length}`);
        this.preloadGalleryNeighbors(state);
      } else {
        interaction.incomingSlide?.remove?.();
      }
      this.clearGalleryPendingVisuals(frame);
      this.galleryInteraction = null;
      return true;
    }

    cancelGalleryGesture({ immediate = false } = {}) {
      const interaction = this.galleryInteraction;
      if (!interaction) return;
      interaction.finished = true;
      interaction.incomingSlide?.removeEventListener?.('transitionend', interaction.transitionListener);
      if (interaction.transitionTimer != null) this.window.clearTimeout?.(interaction.transitionTimer);
      if (this.galleryAnimationFrame != null) this.window.cancelAnimationFrame?.(this.galleryAnimationFrame);
      this.galleryAnimationFrame = null;
      interaction.incomingSlide?.remove?.();
      interaction.frame?.classList?.remove?.('is-gallery-dragging', 'is-gallery-waiting', 'is-gallery-settling');
      interaction.frame?.style?.setProperty?.('--theme-quick-view-gallery-offset', '0px');
      this.clearGalleryPendingVisuals(interaction.frame);
      this.galleryInteraction = null;
      this.galleryInteractionSerial += 1;
    }

    resetQuickViewGestures() {
      this.cancelGalleryGesture({ immediate: true });
      this.galleryGesture = null;
      this.cancelSheetDrag({ immediate: true });
    }

    requestNavigation(direction, source = 'arrow') {
      const interaction = this.prepareGalleryInteraction(direction, source);
      return Boolean(interaction);
    }

    navigateGallery(direction) {
      return this.requestNavigation(direction, 'legacy');
    }

    announceGallery(state, message) {
      const region = state?.root?.querySelector('[data-theme-quick-view-gallery-status]');
      if (region) region.textContent = message;
    }

    resolveVariantForCandidate(position, value) {
      if (!this.productState) return null;
      const candidateSelection = this.productState.selection.slice();
      candidateSelection[position] = value;
      return this.resolveVariant(candidateSelection);
    }

    updatePurchase(root, variant) {
      const idInput = root.querySelector('[data-theme-quick-view-form] [name="id"]');
      const submit = root.querySelector('[data-theme-quick-view-submit]');
      const submitLabel = root.querySelector('[data-theme-quick-view-submit-label]');
      const available = Boolean(variant && variant.available);
      const unresolved = !variant;
      if (idInput) {
        idInput.value = variant ? String(variant.id) : '';
        idInput.disabled = unresolved;
      }
      if (submit) {
        submit.disabled = !available;
        submit.setAttribute('aria-disabled', available ? 'false' : 'true');
      }
      if (submitLabel) {
        const soldOutLabel = submitLabel.getAttribute('data-theme-quick-view-sold-out-label') || 'Sold Out';
        const defaultLabel = submitLabel.getAttribute('data-theme-default-label') || submitLabel.textContent.trim();
        submitLabel.textContent = available ? defaultLabel : (unresolved ? 'Unavailable combination' : soldOutLabel);
      }
    }

    updateMedia(root, media) {
      const container = root.querySelector('[data-theme-quick-view-media]');
      if (!container) return;
      let image = container.querySelector('.theme-quick-view__media-image');
      const placeholder = container.querySelector('[data-theme-quick-view-media-placeholder]');
      if (media && media.src) {
        if (!image) {
          image = this.document.createElement('img');
          image.className = 'theme-quick-view__media-image';
          container.replaceChildren(image);
        }
        image.src = media.src;
        image.alt = media.alt || '';
        if (media.width) image.width = Number(media.width) || image.width;
        if (media.height) image.height = Number(media.height) || image.height;
        image.hidden = false;
        if (placeholder) placeholder.hidden = true;
      } else {
        if (image) image.hidden = true;
        if (placeholder) placeholder.hidden = false;
        if (!placeholder) {
          const fallback = this.document.createElement('span');
          fallback.className = 'theme-quick-view__media-placeholder';
          fallback.setAttribute('data-theme-quick-view-media-placeholder', '');
          fallback.setAttribute('role', 'img');
          fallback.setAttribute('aria-label', 'Product image unavailable');
          fallback.textContent = 'Product image unavailable';
          container.replaceChildren(fallback);
        }
      }
    }

    updateFullLink(root, variant) {
      const links = root.querySelectorAll?.('[data-theme-quick-view-full-link]') || [];
      if (!links.length) return;
      let url;
      try {
        url = new URL(this.productState.productUrl, this.window.location.origin);
        if (variant) url.searchParams.set('variant', String(variant.id));
        else url.searchParams.delete('variant');
        links.forEach((link) => { link.href = url.href; });
      } catch (error) {
        links.forEach((link) => { link.href = this.productState.productUrl; });
      }
    }

    showDialog() {
      this.refreshDialog();
      if (!this.dialog) return;
      this.cancelSheetDrag({ immediate: true });
      this.dialog.setAttribute('aria-modal', 'true');
      this.dialog.classList.add('is-loading');
      this.document.body.classList.add('theme-quick-view-open');
      if (!this.isOpen()) {
        if (typeof this.dialog.showModal === 'function') {
          try {
            this.dialog.showModal();
          } catch (error) {
            this.dialog.setAttribute('open', '');
          }
        } else {
          this.dialog.setAttribute('open', '');
        }
      }
      const closeButton = this.dialog.querySelector('[data-theme-quick-view-close]');
      this.installFocusTrap(closeButton || this.dialog);
    }

    showLoading(trigger) {
      if (!this.dialog) return;
      this.cancelSheetDrag({ immediate: true });
      this.dialog.setAttribute('aria-labelledby', 'ThemeQuickViewTitle');
      const host = this.dialog.querySelector('[data-theme-quick-view-content-host]');
      if (host) {
        host.setAttribute('aria-busy', 'true');
        host.replaceChildren(this.createLoadingFallback(trigger));
      }
      const loading = this.dialog.querySelector('[data-theme-quick-view-loading]');
      if (loading) loading.hidden = false;
      this.refreshFocusTrapKeepingFocus();
      this.announce('Loading Quick View', 'polite');
    }

    createLoadingFallback(trigger) {
      const wrapper = this.document.createElement('p');
      wrapper.className = 'theme-quick-view__noscript-link';
      wrapper.setAttribute('data-theme-quick-view-fallback-wrap', '');
      wrapper.hidden = true;

      const fallback = this.document.createElement('a');
      fallback.setAttribute('data-theme-quick-view-fallback-link', '');
      fallback.href = trigger.getAttribute('data-theme-quick-view-product-url') || trigger.getAttribute('data-product-url') || this.activeFallbackLink?.getAttribute('href') || '#';
      fallback.textContent = this.readFallbackLinkLabel();
      wrapper.append(fallback);
      return wrapper;
    }

    readFallbackLinkLabel() {
      const renderedLink = this.dialog?.querySelector?.('[data-theme-quick-view-fallback-link]');
      const renderedLabel = renderedLink?.textContent?.trim();
      if (renderedLabel) this.fallbackLinkLabel = renderedLabel;
      return this.fallbackLinkLabel || 'View Full Product';
    }

    showError(message) {
      if (!this.dialog) return;
      this.cancelSheetDrag({ immediate: true });
      this.dialog.setAttribute('aria-labelledby', 'ThemeQuickViewTitle');
      const host = this.dialog.querySelector('[data-theme-quick-view-content-host]');
      if (host) {
        host.setAttribute('aria-busy', 'false');
        host.innerHTML = '';
        const error = this.document.createElement('div');
        error.className = 'theme-quick-view__error-state';
        error.setAttribute('data-theme-quick-view-error-state', '');
        error.setAttribute('role', 'alert');
        const text = this.document.createElement('p');
        text.textContent = message || 'Unable to load this product right now.';
        const retry = this.document.createElement('button');
        retry.type = 'button';
        retry.className = 'theme-quick-view__retry';
        retry.setAttribute('data-theme-quick-view-retry', '');
        retry.textContent = 'Try again';
        error.append(text, retry);
        const fallback = this.document.createElement('a');
        fallback.setAttribute('data-theme-quick-view-error-link', '');
        const triggerProductUrl = (trigger) => trigger?.getAttribute?.('data-theme-quick-view-product-url') ||
          trigger?.getAttribute?.('data-product-url') ||
          trigger?.getAttribute?.('data-url') ||
          trigger?.closest?.('[data-theme-product-card]')?.querySelector?.('[data-theme-product-link]')?.getAttribute?.('href');
        fallback.href = triggerProductUrl(this.activeLoadTrigger) || this.activeFallbackLink?.getAttribute('href') || '#';
        fallback.textContent = this.readFallbackLinkLabel();
        error.append(fallback);
        host.append(error);
      }
      this.setHidden('[data-theme-quick-view-loading]', true);
      this.refreshFocusTrapKeepingFocus();
      this.announce(message || 'Unable to load this product right now.', 'assertive');
    }

    retry() {
      if (!this.activeLoadTrigger) return false;
      if (this.activeLoadTrigger === this.activeTrigger) return this.open(this.activeTrigger, { force: true });
      return this.swapFamilyProduct(this.activeLoadTrigger, { force: true });
    }

    swapFamilyProduct(trigger, options = {}) {
      if (!this.isOpen() || !trigger) return false;
      let endpoint;
      try {
        endpoint = this.buildEndpoint(trigger);
      } catch (error) {
        this.showError(error && error.message ? error.message : 'Unable to load this product right now.');
        return false;
      }
      if (this.activeKey !== endpoint.key || options.force) this.abortActiveRequest();
      this.activeLoadTrigger = trigger;
      this.expectedProductId = trigger.getAttribute('data-theme-quick-view-product-id') || '';
      this.pendingSwapFocus = {
        productId: this.expectedProductId,
        restore: Boolean(
          this.document.activeElement === trigger &&
          typeof trigger.getAttribute === 'function' &&
          trigger.getAttribute('data-theme-quick-view-family-swatch') !== null
        )
      };
      this.activeKey = endpoint.key;
      this.requestSerial += 1;
      const token = this.requestSerial;
      this.clearProductState();
      this.clearLayoutFit();
      this.showLoading(trigger);
      const cached = !options.force && this.readCache(endpoint.key);
      if (cached) return this.renderFragment(cached, endpoint.key, token);
      this.loadFragment(endpoint, token, options.force === true);
      return true;
    }

    close(options = {}) {
      if (!this.dialog) return false;
      this.resetQuickViewGestures();
      this.requestSerial += 1;
      this.abortActiveRequest();
      this.clearProductState();
      this.clearGalleryWarmups();
      this.linkedColorFamilyUnion = null;
      this.dialog.classList.remove('is-loading');
      if (typeof this.dialog.close === 'function' && this.isOpen()) this.dialog.close();
      else this.dialog.removeAttribute('open');
      this.document.body.classList.remove('theme-quick-view-open');
      const host = this.dialog.querySelector('[data-theme-quick-view-content-host]');
      if (host) {
        host.replaceChildren();
        host.setAttribute('aria-busy', 'false');
      }
      this.removeFocusTrap();

      const returnFocus = options.returnFocus !== false;
      const trigger = this.activeTrigger;
      const fallback = this.activeFallbackLink;
      this.activeTrigger = null;
      this.activeFallbackLink = null;
      this.activeLoadTrigger = null;
      this.expectedProductId = '';
      this.pendingSwapFocus = null;
      this.activeKey = null;
      if (returnFocus) this.returnFocus(trigger, fallback);
      return true;
    }

    beforeCartRender({ cart } = {}) {
      if (!this.isOpen() || !this.activeTrigger) return false;
      if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(this.activeTrigger);
      this.close({ returnFocus: false });
      return true;
    }

    returnFocus(trigger, fallback) {
      const target = trigger && trigger.isConnected ? trigger : (fallback && fallback.isConnected ? fallback : null);
      if (!target || typeof target.focus !== 'function') return;
      this.focusWithoutScroll(target);
    }

    focusWithoutScroll(target) {
      if (!target || typeof target.focus !== 'function') return;
      this.window.themeFocusModality?.markProgrammaticFocus(target);
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        target.focus();
      }
    }

    findFallbackLink(trigger) {
      return trigger.closest('[data-theme-product-card]')?.querySelector('[data-theme-product-link]') || null;
    }

    abortActiveRequest() {
      const request = this.activeRequest;
      if (!request) return;
      if (request.controller && typeof request.controller.abort === 'function') request.controller.abort();
      const current = this.inflight.get(request.key);
      if (current && current.promise === request.promise) this.inflight.delete(request.key);
      this.activeRequest = null;
    }

    isCurrentRequest(key, token) {
      return this.isOpen() && this.activeKey === key && this.requestSerial === token;
    }

    clearProductState() {
      this.window.themePdpOptionSelect?.destroy(this.productState?.root);
      if (this.lightbox?.open) this.closeLightbox({ restoreFocus: false });
      this.clearLayoutFit();
      const frame = this.productState?.root?.querySelector?.('[data-theme-quick-view-gallery-frame]');
      this.cancelGalleryGesture({ immediate: true });
      this.galleryGesture = null;
      this.clearGalleryPendingVisuals(frame);
      this.productState = null;
    }

    readCache(key) {
      const value = this.cache.get(key);
      if (!value) return null;
      this.cache.delete(key);
      this.cache.set(key, value);
      return value;
    }

    writeCache(key, value) {
      if (!value) return;
      this.cache.delete(key);
      this.cache.set(key, value);
      while (this.cache.size > CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value);
    }

    setHidden(selector, hidden) {
      const element = this.dialog?.querySelector(selector);
      if (element) element.hidden = hidden;
    }

    setText(root, selector, value) {
      const element = root.querySelector(selector);
      if (element) element.textContent = value;
    }

    announce(message, politeness = 'polite') {
      if (!this.dialog) return;
      const region = this.dialog.querySelector('[data-theme-quick-view-shell-status]');
      if (region) {
        region.setAttribute('aria-live', politeness);
        region.textContent = message;
      }
    }

    installFocusTrap(initialFocus) {
      this.removeFocusTrap();
      if (typeof this.window.trapFocus === 'function') {
        this.window.trapFocus(this.dialog, initialFocus || this.dialog);
        return;
      }

      this.fallbackFocusHandler = (event) => {
        if (event.key !== 'Tab' || !this.dialog) return;
        const focusable = Array.from(this.dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && this.document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && this.document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      this.document.addEventListener('keydown', this.fallbackFocusHandler);
      if (initialFocus && typeof initialFocus.focus === 'function') {
        this.window.themeFocusModality?.markProgrammaticFocus(initialFocus);
        initialFocus.focus();
      }
    }

    refreshFocusTrapKeepingFocus() {
      if (!this.dialog) return;
      const closeButton = this.dialog.querySelector('[data-theme-quick-view-close]');
      const active = this.document.activeElement;
      const focusTarget = active && this.dialog.contains(active) ? active : (closeButton || this.dialog);
      this.installFocusTrap(focusTarget);
    }

    removeFocusTrap() {
      if (typeof this.window.removeTrapFocus === 'function') this.window.removeTrapFocus();
      if (this.fallbackFocusHandler) {
        this.document.removeEventListener('keydown', this.fallbackFocusHandler);
        this.fallbackFocusHandler = null;
      }
    }

    errorMessage(error) {
      if (error && error.name === 'AbortError') return 'Unable to load this product right now.';
      return error && error.message ? error.message : 'Unable to load this product right now.';
    }
  }

  const controller = global.__themeQuickViewController && typeof global.__themeQuickViewController.init === 'function' ?
    global.__themeQuickViewController : new ThemeQuickViewController(DOCUMENT, global);
  global.ThemeQuickViewController = ThemeQuickViewController;
  global.themeQuickViewLayoutFit = quickViewLayoutFit;
  global.__themeQuickViewController = controller;
  global.themeQuickViewController = controller;
  controller.init();
})(typeof window !== 'undefined' ? window : globalThis);
